import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import { Rnd } from 'react-rnd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { signPdfWithBridge } from '../utils/pdfSigner';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const BRIDGE_URL = 'http://127.0.0.1:4567';

// Generates a QR code PNG (as Uint8Array) for the given text using an offscreen canvas
// and the browser's built-in fetch of the qrserver.com API, falling back to a plain text box.
const generateQRCodePng = async (text) => {
  return new Promise((resolve, reject) => {
    const size = 200;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))).catch(reject);
      }, 'image/png');
    };
    img.onerror = () => {
      // Fallback: white square with "QR" text if image fails to load
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#000000';
      ctx.font = '14px monospace';
      ctx.fillText('QR unavailable', 10, size / 2);
      canvas.toBlob((blob) => {
        blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))).catch(reject);
      }, 'image/png');
    };
    // Use the QR Server API (free, no key required)
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&margin=1`;
  });
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

const getCleanLowerValue = (value) => String(value || '').split('?')[0].toLowerCase();

const hasImageExtension = (value) => {
  if (!value) return false;
  const clean = getCleanLowerValue(value);
  return IMAGE_EXTENSIONS.some(ext => clean.endsWith(ext));
};

// Helper to check if file is an image
const isImageFile = (url, fileName = '') => hasImageExtension(url) || hasImageExtension(fileName);

const getImageKind = (url, fileName = '') => {
  const value = `${getCleanLowerValue(fileName)}|${getCleanLowerValue(url)}`;
  if (value.includes('.png')) return 'png';
  if (value.includes('.jpg') || value.includes('.jpeg')) return 'jpg';
  return null;
};

const A4_SIZE = { width: 595, height: 842 };

const getFitContain = (srcW, srcH, maxW, maxH) => {
  if (!srcW || !srcH) return { scale: 1, drawW: srcW, drawH: srcH };
  const scale = Math.min(1, maxW / srcW, maxH / srcH);
  return { scale, drawW: srcW * scale, drawH: srcH * scale };
};

const getA4Placement = (srcW, srcH) => {
  const { scale, drawW, drawH } = getFitContain(srcW, srcH, A4_SIZE.width, A4_SIZE.height);
  const x = (A4_SIZE.width - drawW) / 2;
  const y = (A4_SIZE.height - drawH) / 2;
  return { scale, drawW, drawH, x, y };
};

const normalizeRotationAngle = (angle = 0) => {
  const normalized = Number(angle) % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

const getPdfPageGeometry = (page) => {
  const { width, height } = page.getSize();
  const rotation = normalizeRotationAngle(page.getRotation?.().angle || 0);
  const quarterTurn = rotation === 90 || rotation === 270;

  return {
    width,
    height,
    rotation,
    visualWidth: quarterTurn ? height : width,
    visualHeight: quarterTurn ? width : height,
  };
};

// pdf-lib's embedPage() computes its default bounding box via a function that
// hardcodes left/bottom to 0 instead of using the page's actual MediaBox origin
// (a real bug, confirmed against pdf-lib 1.17.1's PDFPageEmbedder.fullPageBoundingBox).
// For pages whose MediaBox doesn't start at (0,0) — some scan-to-PDF tools emit
// e.g. [0, 207.27, 595, 627.58] — this clips the embedded content against the
// wrong box, silently cutting off everything above the page's own height in its
// un-shifted coordinate space (verified: this cut roughly the top half off a
// real scanned page). Passing the real MediaBox explicitly as embedPage's
// boundingBox argument sidesteps the bug entirely.
const getPageMediaBoxBounds = (page) => {
  const { x, y, width, height } = page.getMediaBox();
  return { left: x, bottom: y, right: x + width, top: y + height };
};

// pdf-lib's embedPage()/drawPage() ignore a source page's /Rotate entry entirely —
// embedPage() only ever captures the raw (unrotated) MediaBox content. Without this,
// a page authored/scanned in one orientation but flagged /Rotate 90|180|270 for display
// (which pdf.js/react-pdf honor in the browser preview) gets drawn sideways/stretched
// in the merged output. This reapplies the rotation manually: `placement` is the
// *visual* (post-rotation) box computed by getA4Placement from visualWidth/visualHeight,
// and we rotate+re-anchor the raw (pre-rotation) content so it lands in that same box.
const getRotatedDrawPageParams = (sourceGeometry, placement) => {
  const { width: rawWidth, height: rawHeight, rotation } = sourceGeometry;
  const { scale, x, y, drawW, drawH } = placement;

  // Width/height passed to drawPage always scale pdf-lib's *raw* embeddedPage
  // dimensions (never the visual/swapped ones), regardless of rotation.
  const width = rawWidth * scale;
  const height = rawHeight * scale;

  if (rotation === 90) {
    // Physical clockwise 90° turn == a -90° (270°) rotation in pdf-lib's
    // counter-clockwise content-space convention.
    return { x, y: y + drawH, width, height, rotate: degrees(-90) };
  }
  if (rotation === 180) {
    return { x: x + drawW, y: y + drawH, width, height, rotate: degrees(180) };
  }
  if (rotation === 270) {
    return { x: x + drawW, y, width, height, rotate: degrees(90) };
  }
  return { x, y, width, height, rotate: degrees(0) };
};

const getWidgetRectFromTopLeftStamp = (stamp, pageWidth, pageHeight) => {
  const x1 = Math.max(0, Math.min(pageWidth, stamp.x));
  const top = Math.max(0, Math.min(pageHeight, stamp.y));
  const width = Math.max(0, stamp.width);
  const height = Math.max(0, stamp.height);
  const x2 = Math.max(x1, Math.min(pageWidth, x1 + width));
  const bottom = Math.max(0, Math.min(pageHeight, top + height));
  const y1 = Math.max(0, pageHeight - bottom);
  const y2 = Math.max(y1, Math.min(pageHeight, pageHeight - top));

  return [x1, y1, x2, y2].map((value) => Number(value.toFixed(2)));
};

const CERTIFICATE_DEFAULT_APPEARANCE = {
  includeName: true,
  includeDistinguishedName: true,
  includeDate: true,
  includeLocation: true,
  includeReason: true,
  includeLabels: true,
  includeLogo: false,
  includeAdobeVersion: false,
  textDirection: 'auto',
  logoText: '',
};

const CERTIFICATE_ANCHOR_TYPE = 'certificateAnchor';
const CERTIFICATE_DEFAULT_SIGNER_NAME = 'Maria Jimena MARTINEZ ROJAS';
const CERTIFICATE_DEFAULT_SIGNER_TITLE = 'EXPERT';
const CERTIFICATE_DEFAULT_LOCATION = 'Paris';
const CERTIFICATE_DEFAULT_REASONS = {
  en: 'Legal translation',
  fr: 'Traduction assermentée',
};
const getCertificateDefaultReason = (language = 'en') => (
  String(language || '').toLowerCase().startsWith('fr')
    ? CERTIFICATE_DEFAULT_REASONS.fr
    : CERTIFICATE_DEFAULT_REASONS.en
);
const CERTIFICATE_DEFAULT_REASON_VALUES = new Set(Object.values(CERTIFICATE_DEFAULT_REASONS));

const splitCanvasWord = (ctx, word, maxWidth) => {
  const parts = [];
  let current = '';

  for (const char of String(word || '')) {
    const candidate = `${current}${char}`;
    if (!current || ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      parts.push(current);
      current = char;
    }
  }

  if (current) parts.push(current);
  return parts;
};

const wrapCanvasText = (ctx, text, maxWidth) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (!line || ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      return;
    }

    lines.push(line);
    if (ctx.measureText(word).width <= maxWidth) {
      line = word;
      return;
    }

    const parts = splitCanvasWord(ctx, word, maxWidth);
    lines.push(...parts.slice(0, -1));
    line = parts[parts.length - 1] || '';
  });

  if (line) lines.push(line);
  return lines;
};

const drawRoundRect = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const SignatureStampView = ({
  translations,
  projectName,
  projectId,
  documentHolderName,
  userProfile,
  sourceLanguage,
  targetLanguage,
  onClose,
  onSave,
  onSendToClient = null,
  finalDocuments = [], // Array of existing signed versions
  conformityNumberSeed = projectId
}) => {
  const { googleAccessToken } = useAuth();
  const { t, i18n } = useTranslation();
  const certificateDefaultReason = useMemo(
    () => getCertificateDefaultReason(i18n.resolvedLanguage || i18n.language || 'en'),
    [i18n.resolvedLanguage, i18n.language]
  );
  const [stamps, setStamps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scale, setScale] = useState(0.6);
  const [docPageCounts, setDocPageCounts] = useState({});
  const [docPageDimensions, setDocPageDimensions] = useState({}); // Store actual PDF page dimensions
  const [documentOrder, setDocumentOrder] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side' or 'translation-only'
  const [isDraggingDoc, setIsDraggingDoc] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [pendingStamp, setPendingStamp] = useState(null); // For cursor-based placement
  const [repositioningStamp, setRepositioningStamp] = useState(null); // Track stamp being repositioned (stores original stamp data)
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isValidPlacement, setIsValidPlacement] = useState(false); // Track if cursor is over valid placement area
  const [isResizingStamp, setIsResizingStamp] = useState(false); // Track if currently resizing a stamp
  const [isHoveringStamp, setIsHoveringStamp] = useState(false); // Track if hovering over a stamp
  const [dragStartPos, setDragStartPos] = useState(null); // Track initial mouse position for drag detection
  const [conformityNumber, setConformityNumber] = useState(conformityNumberSeed ? String(conformityNumberSeed).slice(-6) : '');
  const [certificateEnabled, setCertificateEnabled] = useState(false);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [certificateSignerName, setCertificateSignerName] = useState(CERTIFICATE_DEFAULT_SIGNER_NAME);
  const [certificateSignerContact, setCertificateSignerContact] = useState('');
  const [certificateReason, setCertificateReason] = useState(certificateDefaultReason);
  const [certificateLocation, setCertificateLocation] = useState(CERTIFICATE_DEFAULT_LOCATION);
  const [certificateAppearance, setCertificateAppearance] = useState(CERTIFICATE_DEFAULT_APPEARANCE);
  const [certificateError, setCertificateError] = useState('');
  const containerRef = useRef(null);
  const documentRefs = useRef({});
  const pdfDocumentRefs = useRef({}); // Track PDF document instances for cleanup
  const draggedStampRef = useRef(null); // Track stamp being dragged
  const usbPinRef = useRef(''); // Holds USB PIN synchronously for handleSave

  // New state for version management
  const [isSigningNewVersion, setIsSigningNewVersion] = useState(false); // Track if user is signing a new version
  const [selectedVersionId, setSelectedVersionId] = useState(null); // Track which version is being viewed
  const [versionPdfPages, setVersionPdfPages] = useState(0); // Track number of pages in selected version
  const [versionPdfData, setVersionPdfData] = useState({}); // Cache base64 data for version PDFs

  // USB signing bridge state
  const [bridgeAvailable, setBridgeAvailable] = useState(null); // null=detecting, true/false
  const [usbCertificates, setUsbCertificates] = useState([]);
  const [selectedCertId, setSelectedCertId] = useState(null);
  const [selectedCertDerHex, setSelectedCertDerHex] = useState(''); // DER bytes for CMS assembly
  const [certificateParsedInfo, setCertificateParsedInfo] = useState(null);
  const [certificateTitle, setCertificateTitle] = useState(CERTIFICATE_DEFAULT_SIGNER_TITLE);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinValue, setPinValue] = useState('');

  // Load only the selected signed version. Loading every signed PDF eagerly can
  // exhaust browser memory on larger projects.
  React.useEffect(() => {
    if (isSigningNewVersion || !selectedVersionId) {
      setVersionPdfData({});
      setVersionPdfPages(0);
      return;
    }

    const selectedVersion = finalDocuments.find(version => version.id === selectedVersionId);
    if (!selectedVersion?.dropboxUrl) {
      setVersionPdfData({});
      setVersionPdfPages(0);
      return;
    }

    let cancelled = false;

    const loadSelectedVersionPdf = async () => {
      setVersionPdfData({});
      setVersionPdfPages(0);

      try {
        const pdfUrl = selectedVersion.dropboxUrl
          .replace('dl=0', 'dl=1')
          .replace('www.dropbox.com', 'dl.dropboxusercontent.com');
        const response = await fetch(pdfUrl);
        const blob = await response.blob();

        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        if (!cancelled) {
          setVersionPdfData({ [selectedVersion.id]: dataUrl });
        }
      } catch (error) {
        if (!cancelled) {
          console.error(`Failed to load PDF for version ${selectedVersion.version}:`, error);
          setVersionPdfData({});
        }
      }
    };

    loadSelectedVersionPdf();

    return () => {
      cancelled = true;
    };
  }, [finalDocuments, isSigningNewVersion, selectedVersionId]);

  // Initialize mode based on existing versions
  React.useEffect(() => {
    if (finalDocuments && finalDocuments.length > 0) {
      // Sort by version number descending to get latest
      const sortedVersions = [...finalDocuments].sort((a, b) => b.version - a.version);
      const latestVersion = sortedVersions[0];
      setSelectedVersionId(latestVersion.id);
      setIsSigningNewVersion(false);
    } else {
      // No existing versions, enter signing mode directly
      setIsSigningNewVersion(true);
    }
  }, [finalDocuments]);

  React.useEffect(() => {
    setConformityNumber(conformityNumberSeed ? String(conformityNumberSeed).slice(-6) : '');
  }, [conformityNumberSeed]);

  const getVersionDownloadUrl = useCallback((version) => {
    if (!version) return '';
    if (version.downloadUrl) return version.downloadUrl;
    if (version.dropboxUrl) {
      return version.dropboxUrl
        .replace('dl=0', 'dl=1')
        .replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    }
    return '';
  }, []);

  React.useEffect(() => {
    if (!certificateSignerName) {
      const fallbackSignerName = [userProfile?.firstName, userProfile?.lastName]
        .filter(Boolean)
        .join(' ')
        || userProfile?.name
        || userProfile?.email
        || '';
      setCertificateSignerName(fallbackSignerName);
    }
    if (!certificateSignerContact && userProfile?.email) {
      setCertificateSignerContact(userProfile.email);
    }
  }, [certificateSignerName, certificateSignerContact, userProfile]);

  React.useEffect(() => {
    if (!certificateReason) {
      setCertificateReason(certificateDefaultReason);
    } else if (CERTIFICATE_DEFAULT_REASON_VALUES.has(certificateReason) && certificateReason !== certificateDefaultReason) {
      setCertificateReason(certificateDefaultReason);
    }
    if (!certificateLocation) {
      setCertificateLocation(t('signatureStamp.certificate.defaults.location', 'A&H Solutions'));
    }
  }, [certificateReason, certificateDefaultReason, certificateLocation, t]);

  const detectUsbBridge = useCallback(async ({ showChecking = false } = {}) => {
    if (showChecking) {
      setBridgeAvailable(null);
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2500);

    try {
      const statusRes = await fetch(`${BRIDGE_URL}/status`, {
        signal: controller.signal,
      });
      if (!statusRes.ok) throw new Error('Bridge not ready');

      const certsRes = await fetch(`${BRIDGE_URL}/certificates`, {
        signal: controller.signal,
      });
      if (!certsRes.ok) throw new Error('Unable to list USB certificates');

      const certs = await certsRes.json();
      const certificateList = Array.isArray(certs) ? certs : [];
      setBridgeAvailable(true);
      setUsbCertificates(certificateList);

      if (certificateList.length > 0) {
        const selectedCertificate = certificateList[0];
        setSelectedCertId(selectedCertificate.id);
        setSelectedCertDerHex(selectedCertificate.certDerHex || '');
        setCertificateParsedInfo(selectedCertificate);
        setCertificateSignerName((current) => current || selectedCertificate.commonName || '');
        setCertificateTitle((current) => current || selectedCertificate.title || '');
      }

      return true;
    } catch (_) {
      setBridgeAvailable(false);
      setUsbCertificates([]);
      return false;
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  // Detect local USB signing bridge on mount, then retry briefly in case the
  // page loaded before the menu-bar bridge finished starting.
  React.useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let retryTimer = null;

    const detectWithRetry = async () => {
      if (cancelled) return;
      const detected = await detectUsbBridge({ showChecking: attempts === 0 });
      attempts += 1;

      if (!cancelled && !detected && attempts < 10) {
        retryTimer = setTimeout(detectWithRetry, 3000);
      }
    };

    detectWithRetry();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [detectUsbBridge]);

  const getVersionSentLabel = useCallback((sentAt) => {
    if (!sentAt) {
      return t('projectDetails.status.notSent', 'Not sent');
    }
    return `${t('projectDetails.status.sent', 'Sent')} ${new Date(sentAt).toLocaleString()}`;
  }, [t]);

  const isTextStamp = (stamp) => stamp?.renderMode === 'text' || stamp?.type === 'conformityText';
  const isCertificateAnchorStamp = (stamp) => stamp?.type === CERTIFICATE_ANCHOR_TYPE;
  const certificateAnchorStamps = useMemo(
    () => stamps.filter((stamp) => isCertificateAnchorStamp(stamp)),
    [stamps]
  );
  const getCertificateAnchorStamp = useCallback(
    () => certificateAnchorStamps[0] || null,
    [certificateAnchorStamps]
  );

  const formatLanguageCode = (languageCode) => (
    String(languageCode || '--').toUpperCase()
  );

  const buildConformityTextPayload = useCallback((numberValue = conformityNumber, languageOverrides = {}) => {
    const sourceLanguageCode = formatLanguageCode(languageOverrides.sourceLanguage || sourceLanguage);
    const targetLanguageCode = formatLanguageCode(languageOverrides.targetLanguage || targetLanguage);

    return {
      line1: 'VU NE VARIETUR',
      line2: 'Traduction conforme à',
      line3: `l'original en langue ${sourceLanguageCode}`,
      line4: `N° ${numberValue || ''}`,
      sourceLanguageCode,
      targetLanguageCode,
      number: numberValue || ''
    };
  }, [conformityNumber, sourceLanguage, targetLanguage]);

  const getCertificatePreviewLines = useCallback((signingTime = new Date()) => {
    const previewName = certificateSignerName || t('signatureStamp.certificate.preview.signerName', 'Signer Name');
    const showLabel = (label, value) => (
      certificateAppearance.includeLabels ? `${label}: ${value}` : value
    );
    const certLabel = (key, fallback) => t(`signatureStamp.certificate.labels.${key}`, fallback);
    const lines = [];

    if (certificateAppearance.includeName) {
      lines.push(showLabel(certLabel('digitallySignedBy', 'Digitally signed by'), previewName));
    }
    if (certificateAppearance.includeDistinguishedName) {
      lines.push(showLabel(certLabel('distinguishedName', 'Distinguished Name (DN)'), previewName));
    }
    if (certificateAppearance.includeLocation && certificateLocation) {
      lines.push(showLabel(certLabel('location', 'Location'), certificateLocation));
    }
    if (certificateAppearance.includeReason && certificateReason) {
      lines.push(showLabel(certLabel('reason', 'Reason'), certificateReason));
    }
    if (certificateAppearance.includeDate) {
      lines.push(
        showLabel(
          certLabel('date', 'Date'),
          signingTime.toLocaleString(i18n.resolvedLanguage || i18n.language || 'en')
        )
      );
    }
    if (certificateAppearance.includeAdobeVersion) {
      lines.push(showLabel(certLabel('acrobatVersion', 'Adobe Acrobat Version'), 'Acrobat'));
    }
    if (certificateAppearance.includeLogo && certificateAppearance.logoText) {
      lines.push(showLabel(certLabel('logo', 'Logo'), certificateAppearance.logoText));
    }

    return { previewName, lines };
  }, [certificateAppearance, certificateLocation, certificateReason, certificateSignerName, i18n.language, i18n.resolvedLanguage, t]);

  const createCertificateAppearanceImage = useCallback((signingTime = new Date()) => {
    if (typeof document === 'undefined') return null;

    const cssWidth = 340;
    const cssHeight = 130;
    const pixelScale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = cssWidth * pixelScale;
    canvas.height = cssHeight * pixelScale;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.scale(pixelScale, pixelScale);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const leftWidth = cssWidth * 0.4;
    const padding = 8;
    const rightX = leftWidth + padding;
    const rightWidth = cssWidth - rightX - padding;
    const { previewName, lines } = getCertificatePreviewLines(signingTime);

    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, cssWidth - 1, cssHeight - 1);
    ctx.beginPath();
    ctx.moveTo(leftWidth + 0.5, 8);
    ctx.lineTo(leftWidth + 0.5, cssHeight - 8);
    ctx.stroke();

    const watermarkText = 'CERTEUROPE';
    const watermarkCenterX = cssWidth / 2 + 20;
    const watermarkCenterY = 62;
    const watermarkFont = '900 25px Arial, Helvetica, sans-serif';
    ctx.font = watermarkFont;
    const watermarkTextWidth = ctx.measureText(watermarkText).width;
    const watermarkBorderWidth = Math.min(cssWidth - 24, watermarkTextWidth + 32);
    const watermarkBorderX = Math.max(
      12,
      Math.min(cssWidth - watermarkBorderWidth - 12, watermarkCenterX - watermarkBorderWidth / 2)
    );

    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = '#7dc1e4';
    ctx.lineWidth = 3;
    drawRoundRect(ctx, watermarkBorderX, 43, watermarkBorderWidth, 36, 9);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.fillStyle = '#7dbfe3';
    ctx.font = watermarkFont;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(watermarkText, watermarkCenterX, watermarkCenterY);
    ctx.globalAlpha = 0.16;
    ctx.font = '700 15px Arial, Helvetica, sans-serif';
    ctx.fillText('TINEXTA INFOCERT', cssWidth / 2 + 20, 89);
    ctx.globalAlpha = 0.25;
    ctx.font = '700 10px Arial, Helvetica, sans-serif';
    ctx.fillText('CERTEUROPE', cssWidth / 2 + 20, 115);
    ctx.restore();

    ctx.fillStyle = '#111827';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    ctx.font = '700 8px Arial, Helvetica, sans-serif';
    const nameLines = wrapCanvasText(ctx, previewName, leftWidth - padding * 2).slice(0, 4);
    nameLines.forEach((line, index) => {
      ctx.fillText(line, padding, padding + index * 10);
    });

    ctx.font = '400 8px Arial, Helvetica, sans-serif';
    const rightLines = [];
    lines.slice(0, 7).forEach((line) => {
      rightLines.push(...wrapCanvasText(ctx, line, rightWidth));
    });
    rightLines.slice(0, 10).forEach((line, index) => {
      ctx.fillText(line, rightX, padding + index * 10);
    });

    return canvas.toDataURL('image/png', 1.0);
  }, [getCertificatePreviewLines]);

  const certificatePreviewImage = useMemo(
    () => createCertificateAppearanceImage(),
    [createCertificateAppearanceImage]
  );

  const documentPairs = useMemo(() => {
    const pairs = translations.map((translation, index) => {
      const originalUrl = translation.originalUrl || translation.dropboxUrl || translation.dropbox?.originalUrl;
      const convertedOriginalUrl = originalUrl
        ? originalUrl.replace('dl=0', 'dl=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com')
        : null;
      const sourceDocumentId = translation.documentId || translation.id;
      const proxiedOriginalUrl = sourceDocumentId
        ? `${API_URL}/documents/${encodeURIComponent(sourceDocumentId)}/original?preview=true`
        : null;

      const translationFileObj = translation.googleDocId ? {
        url: `https://docs.google.com/document/d/${translation.googleDocId}/export?format=pdf`,
        httpHeaders: googleAccessToken ? { 'Authorization': `Bearer ${googleAccessToken}` } : {},
        withCredentials: false
      } : null;

      return {
        id: translation.id || `doc-${index}`,
        index,
        documentId: translation.documentId,
        originalFileName: translation.originalFileName,
        googleDocId: translation.googleDocId,
        sourceLanguage: translation.sourceLanguage || sourceLanguage,
        targetLanguage: translation.targetLanguage || targetLanguage,
        originalUrl: proxiedOriginalUrl || convertedOriginalUrl,
        originalFallbackUrl: convertedOriginalUrl,
        translationFileObj
      };
    });

    // Apply custom ordering if set, otherwise use original order
    if (documentOrder.length > 0) {
      return documentOrder.map(index => pairs[index]).filter(Boolean);
    }
    return pairs;
  }, [translations, googleAccessToken, documentOrder, sourceLanguage, targetLanguage]);

  const getDocumentPairForStamp = useCallback((stampLike) => (
    documentPairs.find((pair) => pair.id === stampLike?.documentId) || null
  ), [documentPairs]);

  const getConformityTextPayloadForStamp = useCallback((stampLike, pair = null) => {
    const resolvedPair = pair || getDocumentPairForStamp(stampLike);
    if (!resolvedPair) {
      return stampLike?.textPayload || buildConformityTextPayload();
    }

    return buildConformityTextPayload(stampLike?.textPayload?.number || conformityNumber, {
      sourceLanguage: resolvedPair.sourceLanguage,
      targetLanguage: resolvedPair.targetLanguage,
    });
  }, [buildConformityTextPayload, conformityNumber, getDocumentPairForStamp]);

  const languageSummary = useMemo(() => {
    const summarize = (values, fallback) => {
      const uniqueValues = [...new Set(values.filter(Boolean).map(formatLanguageCode))];
      if (uniqueValues.length === 0) return fallback || '—';
      return uniqueValues.join(', ');
    };

    return {
      source: summarize(documentPairs.map((pair) => pair.sourceLanguage), sourceLanguage),
      target: summarize(documentPairs.map((pair) => pair.targetLanguage), targetLanguage),
    };
  }, [documentPairs, sourceLanguage, targetLanguage]);

  const renderStampVisual = (stampLike) => {
    if (isCertificateAnchorStamp(stampLike)) {
      return (
        <img
          src={certificatePreviewImage || ''}
          alt={stampLike.name}
          className="w-full h-full object-fill pointer-events-none select-none"
          draggable={false}
        />
      );
    }

    if (isTextStamp(stampLike)) {
      const payload = getConformityTextPayloadForStamp(stampLike);
      return (
        <div className="w-full h-full bg-transparent border-0 rounded px-1.5 py-1 text-gray-800/90 flex flex-col justify-center pointer-events-none">
          <p className="text-[9px] leading-tight font-extrabold tracking-[0.04em]" style={{ textShadow: '-0.35px -0.35px 0 #374151, 0.35px -0.35px 0 #374151, -0.35px 0.35px 0 #374151, 0.35px 0.35px 0 #374151' }}>{payload.line1}</p>
          <p className="text-[8px] leading-tight font-semibold" style={{ textShadow: '-0.3px -0.3px 0 #4b5563, 0.3px -0.3px 0 #4b5563, -0.3px 0.3px 0 #4b5563, 0.3px 0.3px 0 #4b5563' }}>{payload.line2}</p>
          <p className="text-[8px] leading-tight font-semibold" style={{ textShadow: '-0.3px -0.3px 0 #4b5563, 0.3px -0.3px 0 #4b5563, -0.3px 0.3px 0 #4b5563, 0.3px 0.3px 0 #4b5563' }}>{payload.line3}</p>
          <p className="text-[11px] leading-tight font-extrabold mt-0.5 tracking-[0.02em]" style={{ textShadow: '-0.4px -0.4px 0 #374151, 0.4px -0.4px 0 #374151, -0.4px 0.4px 0 #374151, 0.4px 0.4px 0 #374151' }}>{payload.line4}</p>
        </div>
      );
    }

    return (
      <img
        src={stampLike.src}
        alt={stampLike.name}
        className="w-full h-full object-contain pointer-events-none"
        draggable={false}
      />
    );
  };

  // Initialize document order on mount
  React.useEffect(() => {
    if (translations.length > 0 && documentOrder.length === 0) {
      setDocumentOrder(translations.map((_, idx) => idx));
    }
  }, [translations, documentOrder.length]);

  // Cleanup PDF documents on unmount to prevent worker termination errors
  React.useEffect(() => {
    return () => {
      // Cleanup all PDF document instances
      Object.values(pdfDocumentRefs.current).forEach(doc => {
        if (doc && typeof doc.destroy === 'function') {
          try {
            doc.destroy();
          } catch (err) {
            console.warn('Error destroying PDF document:', err);
          }
        }
      });
      pdfDocumentRefs.current = {};
    };
  }, []);

  const updatePageCount = useCallback((docId, type, numPages) => {
    setDocPageCounts(prev => ({
      ...prev,
      [`${docId}-${type}`]: numPages
    }));
  }, []);

  // Helper function to convert image URL to base64.
  // First tries with crossOrigin = 'anonymous' (works if server sends CORS headers).
  // Falls back to loading without crossOrigin if that fails — the canvas will be
  // "tainted" (toDataURL is blocked for security), but if even that fails we reject.
  const convertImageUrlToBase64 = async (url) => {
    const tryLoad = (withCors) => new Promise((resolve, reject) => {
      const img = new Image();
      if (withCors) img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });

    // Try with CORS first (required for toDataURL to work without taint)
    try {
      return await tryLoad(true);
    } catch (corsError) {
      // Fall back without CORS — image loads but canvas.toDataURL may throw "tainted canvas"
      return await tryLoad(false);
    }
  };

  const addStamp = async (type, clickEvent) => {
    if (clickEvent) {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    }

    if (type === CERTIFICATE_ANCHOR_TYPE) {
      if (clickEvent) {
        const initialX = clickEvent.clientX;
        const initialY = clickEvent.clientY;
        setCursorPosition({ x: initialX, y: initialY });
        const location = detectStampLocation(initialX, initialY);
        setIsValidPlacement(!!location);
      }

      setPendingStamp({
        type: CERTIFICATE_ANCHOR_TYPE,
        renderMode: 'certificate',
        width: 340,
        height: 130,
        name: t('signatureStamp.certificate.anchorName', 'Digital Signature Anchor'),
      });
      return;
    }

    if (type === 'conformityText') {
      if (clickEvent) {
        const initialX = clickEvent.clientX;
        const initialY = clickEvent.clientY;
        setCursorPosition({ x: initialX, y: initialY });
        const location = detectStampLocation(initialX, initialY);
        setIsValidPlacement(!!location);
      }

      setPendingStamp({
        type: 'conformityText',
        renderMode: 'text',
        width: 140,
        height: 70,
        name: t('signatureStamp.sidebar.conformityStamp'),
        textPayload: buildConformityTextPayload()
      });
      return;
    }

    const url = type === 'stamp' ? userProfile?.stampImgUrl : userProfile?.signatureImgUrl;
    if (!url) {
      return;
    }

    const stampUrl = url.replace('dl=0', 'dl=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');

    // Initialize cursor position from button click event
    if (clickEvent) {
      const initialX = clickEvent.clientX;
      const initialY = clickEvent.clientY;
      setCursorPosition({ x: initialX, y: initialY });

      // Check initial placement validity
      const location = detectStampLocation(initialX, initialY);
      setIsValidPlacement(!!location);
    }

    // Convert image to base64 before entering placement mode
    let base64Src = stampUrl;
    try {
      base64Src = await convertImageUrlToBase64(stampUrl);
    } catch (error) {
      console.error('Failed to convert image to base64:', error);
      // Fallback to URL if conversion fails
      base64Src = stampUrl;
    }

    // Enter placement mode instead of directly adding stamp
    setPendingStamp({
      type,
      renderMode: 'image',
      src: base64Src,
      width: type === 'stamp' ? 120 : 180,
      height: type === 'stamp' ? 120 : 60,
      name: type === 'stamp' ? t('signatureStamp.sidebar.officialStamp') : t('signatureStamp.sidebar.signature')
    });
  };

  const removeStamp = (stampId) => {
    setStamps(prev => prev.filter(s => s.id !== stampId));
  };

  const updateStampSize = (stampId, width, height) => {
    setStamps(prev => prev.map(s =>
      s.id === stampId ? { ...s, width, height } : s
    ));
  };

  const getStampMinWidth = (stamp) => {
    if (isCertificateAnchorStamp(stamp)) return 220;
    return isTextStamp(stamp) ? 110 : 60;
  };

  const getStampMinHeight = (stamp) => {
    if (isCertificateAnchorStamp(stamp)) return 110;
    return isTextStamp(stamp) ? 55 : 40;
  };

  const startRepositioning = (stamp, clickEvent) => {
    // Remove stamp from document and attach to cursor for repositioning
    setStamps(prev => prev.filter(s => s.id !== stamp.id));

    // Initialize cursor position from double-click event
    if (clickEvent) {
      const initialX = clickEvent.clientX;
      const initialY = clickEvent.clientY;
      setCursorPosition({ x: initialX, y: initialY });

      // Check initial placement validity
      const location = detectStampLocation(initialX, initialY);
      setIsValidPlacement(!!location);
    }

    // Enter repositioning mode with the stamp's data
    setRepositioningStamp(stamp);
    setPendingStamp({
      type: stamp.type,
      renderMode: stamp.renderMode || (isTextStamp(stamp) ? 'text' : 'image'),
      src: stamp.src,
      width: stamp.width,
      height: stamp.height,
      name: stamp.name,
      textPayload: stamp.textPayload
    });

    // Clear drag detection state
    setDragStartPos(null);
    draggedStampRef.current = null;
  };

  // Handle mouse down on stamp to detect drag intent
  const handleStampMouseDown = (stamp, e) => {
    // Don't detect drag if clicking on delete button or during resize
    if (e.target.closest('button') || isResizingStamp) {
      return;
    }

    // Store initial position and stamp reference
    setDragStartPos({ x: e.clientX, y: e.clientY });
    draggedStampRef.current = stamp;
  };

  // Helper function to detect which page/document the stamp is over
  const detectStampLocation = (stampCenterX, stampCenterY) => {
    if (!containerRef.current) return null;

    // Get all stamp surfaces from both original and translation
    const allSurfaces = containerRef.current.querySelectorAll('[data-stamp-surface]');

    for (const surfaceEl of allSurfaces) {
      const surfaceRect = surfaceEl.getBoundingClientRect();

      // Check if stamp center is within this surface
      if (
        stampCenterX >= surfaceRect.left &&
        stampCenterX <= surfaceRect.right &&
        stampCenterY >= surfaceRect.top &&
        stampCenterY <= surfaceRect.bottom
      ) {
        const pageNumber = parseInt(surfaceEl.getAttribute('data-page-number'));
        const documentId = surfaceEl.getAttribute('data-document-id') || surfaceEl.closest('[data-document-id]')?.getAttribute('data-document-id');
        const documentType = surfaceEl.getAttribute('data-document-type') || surfaceEl.closest('[data-document-type]')?.getAttribute('data-document-type');

        if (!documentId || !documentType || Number.isNaN(pageNumber)) {
          return null;
        }

	        return {
	          documentId,
	          pageIndex: pageNumber - 1,
	          documentType,
	          relativeX: stampCenterX - surfaceRect.left,
	          relativeY: stampCenterY - surfaceRect.top,
	          surfaceWidth: surfaceRect.width,
	          surfaceHeight: surfaceRect.height
	        };
      }
    }

    return null;
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    setIsDraggingDoc(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragEnter = (e, enterIndex) => {
    e.preventDefault();

    if (draggedIndex === null || !isDraggingDoc || draggedIndex === enterIndex) {
      return;
    }

    setDragOverIndex(enterIndex);

    // Swap positions immediately
    const newOrder = [...documentOrder];
    const draggedItem = newOrder[draggedIndex];
    const targetItem = newOrder[enterIndex];

    newOrder[draggedIndex] = targetItem;
    newOrder[enterIndex] = draggedItem;

    setDocumentOrder(newOrder);
    setDraggedIndex(enterIndex);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingDoc(false);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingDoc(false);
  };

  // Handle mouse move for cursor tracking during placement mode
  const handleMouseMove = useCallback((e) => {
    if (pendingStamp) {
      setCursorPosition({ x: e.clientX, y: e.clientY });

      // Check if cursor is over a valid document area
      const location = detectStampLocation(e.clientX, e.clientY);
      setIsValidPlacement(!!location);
    }
  }, [pendingStamp, detectStampLocation]);

  // Handle click to place stamp or cancel
  const handleDocumentClick = useCallback((e) => {
    if (!pendingStamp) return;

    // Only react to clicks inside the document canvas area.
    // This prevents placement from being canceled by sidebar/control clicks.
    if (!containerRef.current || !containerRef.current.contains(e.target)) {
      return;
    }

    // Check if click is on a document page
    const clickX = e.clientX;
    const clickY = e.clientY;
    const location = detectStampLocation(clickX, clickY);

    if (location) {
      // Place or reposition the stamp at clicked location
      const newStamp = {
        id: repositioningStamp ? repositioningStamp.id : `stamp-${Date.now()}-${Math.random()}`,
        type: pendingStamp.type,
        renderMode: pendingStamp.renderMode || (isTextStamp(pendingStamp) ? 'text' : 'image'),
        src: pendingStamp.src,
        x: location.relativeX - pendingStamp.width / 2,
        y: location.relativeY - pendingStamp.height / 2,
        width: pendingStamp.width,
        height: pendingStamp.height,
        documentId: location.documentId,
	        pageIndex: location.pageIndex,
	        documentType: location.documentType,
	        surfaceWidth: location.surfaceWidth,
	        surfaceHeight: location.surfaceHeight,
	        name: pendingStamp.name,
        textPayload: pendingStamp.textPayload
      };

      setStamps(prev => [...prev, newStamp]);
      setPendingStamp(null);
      setRepositioningStamp(null);
    } else {
      // Clicked inside canvas but not on a page surface: keep placement mode active.
      // This avoids accidental cancellation when clicking around the translated view.
      return;
    }
  }, [pendingStamp, repositioningStamp, detectStampLocation]);

  // Add mouse move and click event listeners
  React.useEffect(() => {
    if (pendingStamp) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('click', handleDocumentClick);

      // Add scroll listener to update placement validity during scroll
      const handleScroll = () => {
        if (cursorPosition.x && cursorPosition.y) {
          const location = detectStampLocation(cursorPosition.x, cursorPosition.y);
          setIsValidPlacement(!!location);
        }
      };

      const scrollContainer = containerRef.current;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('click', handleDocumentClick);
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [pendingStamp, handleMouseMove, handleDocumentClick, cursorPosition.x, cursorPosition.y, detectStampLocation]);

  // Handle ESC key to cancel placement or repositioning
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && pendingStamp) {
        if (repositioningStamp) {
          // Restore original stamp if repositioning was cancelled
          setStamps(prev => [...prev, repositioningStamp]);
          setRepositioningStamp(null);
        }
        setPendingStamp(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [pendingStamp, repositioningStamp]);

  // Detect drag intent - if user moves mouse while holding down, start repositioning
  React.useEffect(() => {
    if (!dragStartPos || !draggedStampRef.current) return;

    const handleMouseMove = (e) => {
      if (!dragStartPos || !draggedStampRef.current) return;

      // Calculate distance moved from initial position
      const deltaX = e.clientX - dragStartPos.x;
      const deltaY = e.clientY - dragStartPos.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // If moved more than 15 pixels, it's a drag intent - start repositioning
      if (distance > 15) {
        startRepositioning(draggedStampRef.current, e);
      }
    };

    const handleMouseUp = () => {
      // Clear drag detection state
      setDragStartPos(null);
      draggedStampRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragStartPos]);

  const handleSave = async () => {
    setSaving(true);
    setCertificateError('');
    try {
      const certificateAnchorStamp = getCertificateAnchorStamp();

      const isUsbModeActive = certificateEnabled && bridgeAvailable === true && !!selectedCertId;
      const isUsbMode = isUsbModeActive && !!selectedCertDerHex;
      if (certificateEnabled && !isUsbModeActive && !certificateFile) {
        throw new Error(t('signatureStamp.certificate.errors.missingFile', 'Please upload a .p12/.pfx certificate file before finalizing.'));
      }
      if (isUsbModeActive && !selectedCertDerHex) {
        throw new Error('Certificate data not available. Please restart the Juree Signing Bridge and try again.');
      }

      if (certificateEnabled && !certificateAnchorStamp) {
        throw new Error(t('signatureStamp.certificate.errors.missingAnchor', 'Please place the Digital Signature Anchor on the document.'));
      }

      const signingTime = new Date();
      const signatureAppearanceImage = certificateEnabled
        ? createCertificateAppearanceImage(signingTime)
        : null;

      // Create a single merged PDF document
      const finalMergedPdf = await PDFDocument.create();
      let certificateAnchorPayload = null;
      let contentPageCursor = 0;
      const conformityFontCache = new WeakMap();
      const getConformityBoldFont = async (pdfDoc) => {
        if (!conformityFontCache.has(pdfDoc)) {
          const embedded = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
          conformityFontCache.set(pdfDoc, embedded);
        }
        return conformityFontCache.get(pdfDoc);
      };

      const fitSize = (fontRef, text, maxWidth, initialSize, minSize = 6) => {
        let size = initialSize;
        while (size > minSize && fontRef.widthOfTextAtSize(text, size) > maxWidth) {
          size -= 0.25;
        }
        return size;
      };

      const getStampScaleForPage = (stamp, pageWidth, pageHeight, fallbackRenderedWidth = null) => {
        const sourceWidth = Number(stamp.surfaceWidth) || fallbackRenderedWidth || pageWidth;
        const sourceHeight = Number(stamp.surfaceHeight) || (sourceWidth ? sourceWidth * (pageHeight / pageWidth) : pageHeight);
        return {
          xScale: pageWidth / sourceWidth,
          yScale: pageHeight / sourceHeight,
        };
      };

      const drawCertificateAnchorOnPage = async (pdfDoc, page, stamp, xScale, yScale = xScale, contentBox = null) => {
        const { width: pageWidth, height: pageHeight } = page.getSize();
        // contentBox is the actual drawn footprint of the source content within
        // `page` (see getA4Placement) — it's smaller than the full page whenever
        // the source is smaller than A4 (getA4Placement never upscales). Stamp
        // coordinates are relative to that footprint, not the full page, so its
        // origin/height must be folded in here rather than assuming page.getSize()
        // is the same box the stamp was placed against. Defaults to the full page
        // for callers (translation pages) where the content already fills it.
        const originX = contentBox?.x || 0;
        const originY = contentBox?.y || 0;
        const boxHeight = contentBox?.drawH ?? pageHeight;
        const pdfX = Math.max(0, originX + stamp.x * xScale);
        const pdfTop = Math.min(pageHeight, originY + boxHeight - (stamp.y * yScale));
        const pdfWidth = Math.max(120, stamp.width * xScale);
        const pdfHeight = Math.max(54, stamp.height * yScale);
        const safeWidth = Math.min(pdfWidth, pageWidth - pdfX);
        const safeHeight = Math.min(pdfHeight, pdfTop);
        const pdfY = Math.max(0, pdfTop - safeHeight);

        if (signatureAppearanceImage) {
          const base64 = signatureAppearanceImage.split(',')[1];
          const imageBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
          const image = await pdfDoc.embedPng(imageBytes);
          page.drawImage(image, {
            x: pdfX,
            y: pdfY,
            width: safeWidth,
            height: safeHeight,
          });
          return;
        }

        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const padding = Math.max(4, 6 * Math.min(xScale, yScale));
        const leftColWidth = Math.max(64, safeWidth * 0.4);
        const rightX = pdfX + leftColWidth + padding;
        const rightWidth = Math.max(24, safeWidth - leftColWidth - padding * 2);
        const previewName = certificateSignerName || t('signatureStamp.certificate.preview.signerName', 'Signer Name');
        const signedAt = new Date().toLocaleString(i18n.resolvedLanguage || i18n.language || 'en');
        const showLabel = (label, value) => (
          certificateAppearance.includeLabels ? `${label}: ${value}` : value
        );
        const certLabel = (key, fallback) => t(`signatureStamp.certificate.labels.${key}`, fallback);
        const previewLines = [];

        if (certificateAppearance.includeName) {
          previewLines.push(showLabel(certLabel('digitallySignedBy', 'Digitally signed by'), previewName));
        }
        if (certificateAppearance.includeDistinguishedName) {
          previewLines.push(showLabel(certLabel('distinguishedName', 'Distinguished Name (DN)'), previewName));
        }
        if (certificateAppearance.includeLocation && certificateLocation) {
          previewLines.push(showLabel(certLabel('location', 'Location'), certificateLocation));
        }
        if (certificateAppearance.includeReason && certificateReason) {
          previewLines.push(showLabel(certLabel('reason', 'Reason'), certificateReason));
        }
        if (certificateAppearance.includeDate) {
          previewLines.push(showLabel(certLabel('date', 'Date'), signedAt));
        }

        page.drawRectangle({
          x: pdfX,
          y: pdfY,
          width: safeWidth,
          height: safeHeight,
          borderColor: rgb(0.74, 0.74, 0.74),
          borderWidth: 0.8,
        });
        page.drawLine({
          start: { x: pdfX + leftColWidth, y: pdfY + padding },
          end: { x: pdfX + leftColWidth, y: pdfY + safeHeight - padding },
          thickness: 0.5,
          color: rgb(0.74, 0.74, 0.74),
        });

        const watermarkText = 'CERTEUROPE';
        const watermarkSize = fitSize(bold, watermarkText, safeWidth * 0.72, Math.min(22, safeHeight * 0.24), 8);
        page.drawText(watermarkText, {
          x: pdfX + safeWidth * 0.18,
          y: pdfY + safeHeight * 0.38,
          size: watermarkSize,
          font: bold,
          color: rgb(0.78, 0.88, 0.96),
          opacity: 0.42,
        });

        const nameSize = fitSize(bold, previewName, Math.max(16, leftColWidth - padding * 2), Math.min(11, safeHeight / 8), 6);
        page.drawText(previewName, {
          x: pdfX + padding,
          y: Math.max(pdfY + padding, pdfY + safeHeight - padding - nameSize),
          size: nameSize,
          font: bold,
          color: rgb(0.12, 0.12, 0.12),
        });

        const lineHeight = Math.max(7, Math.min(11, safeHeight / 7));
        let textY = pdfY + safeHeight - padding - lineHeight;
        previewLines.slice(0, 7).forEach((line) => {
          let display = String(line || '');
          const fontSize = fitSize(font, display, rightWidth, Math.min(8, lineHeight * 0.85), 5);
          while (display.length > 1 && font.widthOfTextAtSize(display, fontSize) > rightWidth) {
            display = display.slice(0, -1);
          }
          page.drawText(display, {
            x: rightX,
            y: Math.max(pdfY + padding, textY),
            size: fontSize,
            font,
            color: rgb(0.25, 0.25, 0.25),
          });
          textY -= lineHeight;
        });
      };

      const drawConformityStampOnPage = async (pdfDoc, page, stamp, xScale, yScale = xScale, contentBox = null, pair = null) => {
        const payload = getConformityTextPayloadForStamp(stamp, pair);
        const { width: pageWidth, height: pageHeight } = page.getSize();
        const conformityFontBold = await getConformityBoldFont(pdfDoc);
        // See drawCertificateAnchorOnPage's comment: contentBox is the actual
        // drawn footprint of the source content, which stamp coordinates are
        // relative to — not necessarily the full page.
        const originX = contentBox?.x || 0;
        const originY = contentBox?.y || 0;
        const boxHeight = contentBox?.drawH ?? pageHeight;
        const pdfX = Math.max(0, originX + stamp.x * xScale);
        const pdfTop = Math.min(pageHeight, originY + boxHeight - (stamp.y * yScale));
        const pdfWidth = Math.max(80, stamp.width * xScale);
        const pdfHeight = Math.max(40, stamp.height * yScale);
        const safeWidth = Math.min(pdfWidth, pageWidth - pdfX);
        const safeHeight = Math.min(pdfHeight, pdfTop);

        const paddingX = Math.max(4, 8 * xScale);
        const paddingY = Math.max(3, 6 * yScale);
        const textX = pdfX + paddingX;
        const contentWidth = Math.max(20, safeWidth - paddingX * 2);
        const lineGap = Math.max(8, safeHeight / 5.2);
        const textColor = rgb(0.2, 0.2, 0.2);
        const textBorderColor = rgb(0.12, 0.12, 0.12);

        const lines = [payload.line1, payload.line2, payload.line3, payload.line4];
        let textY = pdfTop - paddingY - lineGap;
        const preferredSizes = [lineGap * 0.9, lineGap * 0.78, lineGap * 0.78, lineGap * 1.02];
        const fonts = [conformityFontBold, conformityFontBold, conformityFontBold, conformityFontBold];

        lines.forEach((line, index) => {
          const safeLine = String(line || '');
          const fontRef = fonts[index];
          const fontSize = fitSize(fontRef, safeLine, contentWidth, preferredSizes[index]);
          const strokeOffset = Math.max(0.3, fontSize * 0.03);

          // Fake text border/outline by drawing darker text around the glyphs
          page.drawText(safeLine, { x: textX - strokeOffset, y: Math.max(0, textY), size: fontSize, font: fontRef, color: textBorderColor });
          page.drawText(safeLine, { x: textX + strokeOffset, y: Math.max(0, textY), size: fontSize, font: fontRef, color: textBorderColor });
          page.drawText(safeLine, { x: textX, y: Math.max(0, textY - strokeOffset), size: fontSize, font: fontRef, color: textBorderColor });
          page.drawText(safeLine, { x: textX, y: Math.max(0, textY + strokeOffset), size: fontSize, font: fontRef, color: textBorderColor });

          page.drawText(safeLine, {
            x: textX,
            y: Math.max(0, textY),
            size: fontSize,
            font: fontRef,
            color: textColor
          });
          textY -= lineGap;
        });
      };

      const fetchArrayBufferWithFallback = async (primaryUrl, fallbackUrl = null) => {
        if (!primaryUrl) throw new Error('Missing URL');
        const urls = [primaryUrl];
        if (fallbackUrl && fallbackUrl !== primaryUrl) urls.push(fallbackUrl);
        let lastError = null;

        for (const url of urls) {
          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            return await response.arrayBuffer();
          } catch (error) {
            lastError = error;
          }
        }

        throw lastError || new Error('Failed to fetch file');
      };

      const embedImageWithDetection = async (pdfDoc, imageBytes, imageKind = null) => {
        if (imageKind === 'png') return pdfDoc.embedPng(imageBytes);
        if (imageKind === 'jpg') return pdfDoc.embedJpg(imageBytes);

        try {
          return await pdfDoc.embedPng(imageBytes);
        } catch (_pngError) {
          return pdfDoc.embedJpg(imageBytes);
        }
      };

      for (const pair of documentPairs) {
        // Skip if no translation
        if (!pair.translationFileObj) continue;

        // Fetch original PDF/image and translation PDF
        let originalPdfDoc = null;
        let originalPages = [];
        let originalIsImage = false;
        let originalImageBytes = null;
        let originalImageDims = null;

        // Load original document if available
        if (pair.originalUrl) {
          const originalLooksLikeImage = isImageFile(pair.originalUrl, pair.originalFileName);
          if (originalLooksLikeImage) {
            try {
              originalImageBytes = await fetchArrayBufferWithFallback(pair.originalUrl, pair.originalFallbackUrl);
              const tempDoc = await PDFDocument.create();
              const kind = getImageKind(pair.originalUrl, pair.originalFileName);
              const tempImage = await embedImageWithDetection(tempDoc, originalImageBytes, kind);
              originalImageDims = tempImage.scale(1);
              originalIsImage = true;
              originalPages = [true];
            } catch (error) {
              console.error(`Failed to load original image for pair ${pair.id}:`, error);
            }
          } else {
            try {
              const origPdfBytes = await fetchArrayBufferWithFallback(pair.originalUrl, pair.originalFallbackUrl);
              originalPdfDoc = await PDFDocument.load(origPdfBytes);
              originalPages = originalPdfDoc.getPages();
            } catch (error) {
              console.error(`Failed to load original PDF for pair ${pair.id}:`, error);
            }
          }
        }

        // The original page/image is drawn into the A4 output page via getA4Placement's
        // fit-contain box, which is smaller than the full page whenever the source is
        // smaller than A4 (e.g. an A5-sized scan) — getA4Placement caps its scale at 1
        // to avoid upscaling. Stamp coordinates are recorded relative to that same
        // fit-contain box in the browser (see the data-stamp-surface sizing), so any
        // stamp math that uses the full output page's dimensions as its reference
        // instead of this box will land stamps outside the actual visible content
        // whenever the two don't match. This computes that box for a given original
        // page index so all stamp math below can be anchored to it consistently.
        const getOriginalContentBox = (pageIndex) => {
          if (originalIsImage && originalImageDims) {
            return getA4Placement(originalImageDims.width, originalImageDims.height);
          }
          const page = originalPdfDoc ? originalPages[pageIndex] : null;
          if (!page) return null;
          const geometry = getPdfPageGeometry(page);
          return getA4Placement(geometry.visualWidth, geometry.visualHeight);
        };

        // Fetch the translation PDF
        const transResponse = await fetch(pair.translationFileObj.url, {
          headers: pair.translationFileObj.httpHeaders || {}
        });
        const transPdfBytes = await transResponse.arrayBuffer();
        const transPdfDoc = await PDFDocument.load(transPdfBytes);
        const transPages = transPdfDoc.getPages();

        // Get stamps for this document (both original and translation)
        const originalStamps = stamps.filter(s => s.documentId === pair.id && s.documentType === 'original');
        const translationStamps = stamps.filter(s => s.documentId === pair.id && s.documentType === 'translation');

        const hasOriginalPages = (originalPdfDoc || originalIsImage) && originalPages.length > 0;
        const originalBaseIndex = contentPageCursor;
        const translationBaseIndex = originalBaseIndex + (hasOriginalPages ? originalPages.length : 0);

        if (
          certificateAnchorStamp &&
          certificateAnchorStamp.documentId === pair.id &&
          !certificateAnchorPayload
	        ) {
	          if (certificateAnchorStamp.documentType === 'original' && hasOriginalPages) {
	            if (certificateAnchorStamp.pageIndex >= originalPages.length) {
	              throw new Error('Digital signature anchor page is out of range.');
	            }
	
	            const anchorPageWidth = A4_SIZE.width;
	            const anchorPageHeight = A4_SIZE.height;
	            const anchorContentBox = getOriginalContentBox(certificateAnchorStamp.pageIndex);
	            const anchorReferenceWidth = anchorContentBox?.drawW || anchorPageWidth;
	            const anchorReferenceHeight = anchorContentBox?.drawH || anchorPageHeight;
	            const anchorOriginX = anchorContentBox?.x || 0;
	            const anchorOriginY = anchorContentBox?.y || 0;
	            const { xScale, yScale } = getStampScaleForPage(certificateAnchorStamp, anchorReferenceWidth, anchorReferenceHeight);
	            const anchorStampForOutput = {
	              ...certificateAnchorStamp,
	              x: anchorOriginX + certificateAnchorStamp.x * xScale,
	              y: (anchorPageHeight - anchorOriginY - anchorReferenceHeight) + certificateAnchorStamp.y * yScale,
	              width: certificateAnchorStamp.width * xScale,
	              height: certificateAnchorStamp.height * yScale,
	            };

	            certificateAnchorPayload = {
	              pageIndex: originalBaseIndex + certificateAnchorStamp.pageIndex,
	              widgetRect: getWidgetRectFromTopLeftStamp(anchorStampForOutput, anchorPageWidth, anchorPageHeight),
	            };
          }

          if (certificateAnchorStamp.documentType === 'translation') {
            const anchorPage = transPages[certificateAnchorStamp.pageIndex];
            if (!anchorPage) {
              throw new Error('Digital signature anchor translation page is out of range.');
            }

	            const { width: anchorPageWidth, height: anchorPageHeight } = anchorPage.getSize();
	            const docDimensions = docPageDimensions[pair.id];
	            const renderedWidth = docDimensions?.renderedWidth || (window.innerWidth - 350) / 2;
	            const { xScale, yScale } = getStampScaleForPage(certificateAnchorStamp, anchorPageWidth, anchorPageHeight, renderedWidth);
	
	            const x1 = certificateAnchorStamp.x * xScale;
	            const y1 = anchorPageHeight - (certificateAnchorStamp.y * yScale) - (certificateAnchorStamp.height * yScale);
	            const x2 = x1 + certificateAnchorStamp.width * xScale;
	            const y2 = y1 + certificateAnchorStamp.height * yScale;

            certificateAnchorPayload = {
              pageIndex: translationBaseIndex + certificateAnchorStamp.pageIndex,
              widgetRect: [x1, y1, x2, y2].map((value) => Number(value.toFixed(2))),
            };
          }
        }

        // Add stamps to translation pages
        for (const stamp of translationStamps) {
          if (stamp.pageIndex >= transPages.length) continue;

	          const page = transPages[stamp.pageIndex];
	          const { width: pageWidth, height: pageHeight } = page.getSize();
	
	          // Calculate scaling based on the exact surface the stamp was placed on.
	          const docDimensions = docPageDimensions[pair.id];
	          const renderedWidth = docDimensions?.renderedWidth || (window.innerWidth - 350) / 2;
	          const { xScale, yScale } = getStampScaleForPage(stamp, pageWidth, pageHeight, renderedWidth);
	
	          if (isTextStamp(stamp)) {
	            await drawConformityStampOnPage(transPdfDoc, page, stamp, xScale, yScale, null, pair);
	            continue;
	          }
	
	          if (isCertificateAnchorStamp(stamp)) {
	            if (certificateEnabled && certificateAnchorStamp?.id === stamp.id) {
	              continue;
	            }
	            await drawCertificateAnchorOnPage(transPdfDoc, page, stamp, xScale, yScale);
	            continue;
	          }

          // Get stamp image bytes
          let stampBytes;

          // Check if stamp.src is base64 or URL
          if (stamp.src.startsWith('data:image')) {
            // It's base64 - convert to ArrayBuffer
            const base64Data = stamp.src.split(',')[1];
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            stampBytes = bytes.buffer;
          } else {
            // It's a URL - fetch it (fallback for old stamps)
            const stampResponse = await fetch(stamp.src);
            stampBytes = await stampResponse.arrayBuffer();
          }

          // Embed image based on type
          let stampImage;
          // For base64, we always use PNG since that's what we convert to
          if (stamp.src.startsWith('data:image/png') || stamp.src.toLowerCase().includes('.png')) {
            stampImage = await transPdfDoc.embedPng(stampBytes);
          } else {
            stampImage = await transPdfDoc.embedJpg(stampBytes);
          }

          // Get the intrinsic dimensions of the stamp image
          const stampDims = stampImage.scale(1);
          const aspectRatio = stampDims.width / stampDims.height;

          // Calculate position using the correct scaling factor
	          const pdfX = stamp.x * xScale;
	          const pdfY = pageHeight - (stamp.y * yScale) - (stamp.height * yScale);
	          const pdfWidth = stamp.width * xScale;
	          const pdfHeight = stamp.height * yScale;

          // Maintain aspect ratio by scaling proportionally
          let finalWidth = pdfWidth;
          let finalHeight = pdfHeight;

          // Adjust dimensions to maintain aspect ratio
          const currentAspectRatio = pdfWidth / pdfHeight;
          if (currentAspectRatio > aspectRatio) {
            // Too wide, constrain by height
            finalWidth = pdfHeight * aspectRatio;
          } else {
            // Too tall, constrain by width
            finalHeight = pdfWidth / aspectRatio;
          }

          // Draw stamp on page
          page.drawImage(stampImage, {
            x: pdfX,
            y: pdfY,
            width: finalWidth,
            height: finalHeight,
          });
        }

	        // Append all original pages first, then all translation pages for this document pair.
	        if (hasOriginalPages) {
	          for (let pageIndex = 0; pageIndex < originalPages.length; pageIndex++) {
	            const sourcePage = originalPdfDoc ? originalPages[pageIndex] : null;
	            const sourceGeometry = sourcePage ? getPdfPageGeometry(sourcePage) : null;
	            const outputPage = finalMergedPdf.addPage([A4_SIZE.width, A4_SIZE.height]);

	            const contentBox = getOriginalContentBox(pageIndex);

	            if (originalIsImage && originalImageBytes && originalImageDims) {
	              const { x, y, drawW, drawH } = contentBox;
	              const kind = getImageKind(pair.originalUrl, pair.originalFileName);
	              const image = await embedImageWithDetection(finalMergedPdf, originalImageBytes, kind);
	              outputPage.drawImage(image, { x, y, width: drawW, height: drawH });
	            } else if (sourcePage && sourceGeometry) {
	              const embeddedPage = await finalMergedPdf.embedPage(sourcePage, getPageMediaBoxBounds(sourcePage));
	              outputPage.drawPage(embeddedPage, getRotatedDrawPageParams(sourceGeometry, contentBox));
	            }

	            const pageStamps = originalStamps.filter(s => s.pageIndex === pageIndex);
	            for (const stamp of pageStamps) {
	              const { width: outputPageWidth, height: outputPageHeight } = outputPage.getSize();
	              // Stamps are recorded relative to the actual drawn content box (see
	              // getOriginalContentBox above), not the full output page — scale
	              // against that box's size, not the page's, or stamps land outside
	              // the visible content whenever the two don't match (e.g. an
	              // A5-sized original centered within the A4 output page).
	              const referenceWidth = contentBox?.drawW || outputPageWidth;
	              const referenceHeight = contentBox?.drawH || outputPageHeight;
	              const { xScale, yScale } = getStampScaleForPage(stamp, referenceWidth, referenceHeight);

	              if (isTextStamp(stamp)) {
	                await drawConformityStampOnPage(finalMergedPdf, outputPage, stamp, xScale, yScale, contentBox, pair);
	                continue;
	              }

	              if (isCertificateAnchorStamp(stamp)) {
	                if (certificateEnabled && certificateAnchorStamp?.id === stamp.id) {
	                  continue;
	                }
	                await drawCertificateAnchorOnPage(finalMergedPdf, outputPage, stamp, xScale, yScale, contentBox);
	                continue;
	              }

              // Get stamp image bytes
              let stampBytes;
              if (stamp.src.startsWith('data:image')) {
                const base64Data = stamp.src.split(',')[1];
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                stampBytes = bytes.buffer;
              } else {
                const stampResponse = await fetch(stamp.src);
                stampBytes = await stampResponse.arrayBuffer();
              }

              // Embed image on final merged PDF
              let stampImage;
              if (stamp.src.startsWith('data:image/png') || stamp.src.toLowerCase().includes('.png')) {
                stampImage = await finalMergedPdf.embedPng(stampBytes);
              } else {
                stampImage = await finalMergedPdf.embedJpg(stampBytes);
              }

              const stampDims = stampImage.scale(1);
              const aspectRatio = stampDims.width / stampDims.height;

	              const contentOriginX = contentBox?.x || 0;
	              const contentOriginY = contentBox?.y || 0;
	              const pdfX = Math.max(0, Math.min(outputPageWidth, contentOriginX + stamp.x * xScale));
	              const pdfY = Math.max(0, contentOriginY + referenceHeight - (stamp.y * yScale) - (stamp.height * yScale));
	              const pdfWidth = stamp.width * xScale;
	              const pdfHeight = stamp.height * yScale;

              let finalWidth = pdfWidth;
              let finalHeight = pdfHeight;

              const currentAspectRatio = pdfWidth / pdfHeight;
              if (currentAspectRatio > aspectRatio) {
                finalWidth = pdfHeight * aspectRatio;
              } else {
                finalHeight = pdfWidth / aspectRatio;
              }

              const safeFinalWidth = Math.max(0, Math.min(finalWidth, outputPageWidth - pdfX));
              const safeFinalHeight = Math.max(0, Math.min(finalHeight, outputPageHeight - pdfY));
              if (safeFinalWidth <= 0 || safeFinalHeight <= 0) {
                continue;
              }

              outputPage.drawImage(stampImage, {
                x: pdfX,
                y: pdfY,
                width: safeFinalWidth,
                height: safeFinalHeight,
              });
            }
          }
        }

        if (transPages.length > 0) {
          const translationPageIndices = Array.from({ length: transPages.length }, (_, i) => i);
          const copiedTransPages = await finalMergedPdf.copyPages(transPdfDoc, translationPageIndices);
          copiedTransPages.forEach(p => finalMergedPdf.addPage(p));
        }

        contentPageCursor += (hasOriginalPages ? originalPages.length : 0) + transPages.length;
      }

      if (certificateEnabled && !certificateAnchorPayload) {
        throw new Error(t('signatureStamp.certificate.errors.resolveAnchor', 'Could not resolve digital signature anchor location.'));
      }

      // Save the merged PDF (content pages only, no auth page yet)
      const mergedPdfBytes = await finalMergedPdf.save({ updateMetadata: false });

      // --- Compute SHA-256 hash of the content PDF bytes ---
      const hashBuffer = await crypto.subtle.digest('SHA-256', mergedPdfBytes);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // --- Build verification URL encoding both the project and the hash ---
      const appBaseUrl = window.location.origin;
      const verificationUrl = `${appBaseUrl}/verify/${projectId}?hash=${hashHex}`;

      // --- Generate QR code PNG via canvas (no external library needed) ---
      const qrBytes = await generateQRCodePng(verificationUrl);

      // --- Build final PDF: copy content pages + append auth page ---
      const outputPdf = await PDFDocument.create();

      // Copy all content pages
      const contentPageIndices = Array.from({ length: finalMergedPdf.getPageCount() }, (_, i) => i);
      const copiedPages = await outputPdf.copyPages(finalMergedPdf, contentPageIndices);
      copiedPages.forEach(p => outputPdf.addPage(p));

      // --- Authentication Certificate Page ---
      const authPage = outputPdf.addPage([595, 842]); // A4 portrait
      const { width: pageW, height: pageH } = authPage.getSize();
      const font = await outputPdf.embedFont(StandardFonts.Helvetica);
      const fontBold = await outputPdf.embedFont(StandardFonts.HelveticaBold);
      const fontMono = await outputPdf.embedFont(StandardFonts.Courier);

      const margin = 50;
      let cursorY = pageH - margin;

      // Title bar background
      authPage.drawRectangle({
        x: 0,
        y: pageH - 80,
        width: pageW,
        height: 80,
        color: rgb(0.1, 0.2, 0.5),
      });

      // Title text
      authPage.drawText('Document Authentication Certificate', {
        x: margin,
        y: pageH - 52,
        size: 18,
        font: fontBold,
        color: rgb(1, 1, 1),
      });

      cursorY = pageH - 110;

      // Helper to draw a label+value row
      const drawRow = (label, value, y, valueFont = font, valueSize = 11) => {
        authPage.drawText(label, { x: margin, y, size: 11, font: fontBold, color: rgb(0.2, 0.2, 0.2) });
        authPage.drawText(value, { x: margin + 160, y, size: valueSize, font: valueFont, color: rgb(0.1, 0.1, 0.1) });
      };

      const signerName = [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email || 'Unknown';
      const signedAt = new Date().toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' });

      const holderName = documentHolderName || projectName || '—';
      drawRow('Document Holder:', holderName, cursorY);
      cursorY -= 22;
      drawRow('Signed by:', signerName, cursorY);
      cursorY -= 22;
      drawRow('Date & Time:', signedAt, cursorY);
      cursorY -= 22;
      drawRow('Source Language:', languageSummary.source, cursorY);
      cursorY -= 22;
      drawRow('Target Language:', languageSummary.target, cursorY);
     
      cursorY -= 30;

      // Divider
      authPage.drawLine({
        start: { x: margin, y: cursorY },
        end: { x: pageW - margin, y: cursorY },
        thickness: 1,
        color: rgb(0.7, 0.7, 0.7),
      });

      cursorY -= 24;

      // Hash section header
      authPage.drawText('Document Integrity Hash (SHA-256)', {
        x: margin, y: cursorY, size: 12, font: fontBold, color: rgb(0.1, 0.2, 0.5),
      });

      cursorY -= 18;

      // Hash box background
      authPage.drawRectangle({
        x: margin,
        y: cursorY - 10,
        width: pageW - margin * 2,
        height: 22,
        color: rgb(0.95, 0.95, 0.97),
        borderColor: rgb(0.7, 0.7, 0.8),
        borderWidth: 1,
      });

      // Hash value (split into two lines of 32 chars each for readability)
      const hashLine1 = hashHex.slice(0, 32);
      const hashLine2 = hashHex.slice(32);
      authPage.drawText(`${hashLine1}  ${hashLine2}`, {
        x: margin + 6, y: cursorY - 3, size: 9, font: fontMono, color: rgb(0.1, 0.1, 0.1),
      });

      cursorY -= 40;

      // QR Code section
      authPage.drawText('Scan to verify this document online:', {
        x: margin, y: cursorY, size: 11, font: fontBold, color: rgb(0.2, 0.2, 0.2),
      });

      cursorY -= 8;

      const qrImage = await outputPdf.embedPng(qrBytes);
      const qrSize = 140;
      authPage.drawImage(qrImage, {
        x: margin,
        y: cursorY - qrSize,
        width: qrSize,
        height: qrSize,
      });

      // Verification note next to QR code
      const noteX = margin + qrSize + 20;
      const noteLines = [
        'Scan the QR code or visit the URL',
        'below to verify this document online.',
        '',
        'The page confirms:',
        '- Document holder & translator',
        '- Certification date & languages',
        '- SHA-256 integrity hash',
        '',
        'To verify the PDF has not been altered:',
        '1. Compute SHA-256 of all pages',
        '   except this certificate page.',
        '2. Compare with the hash above.',
        '',
        'Any modification will produce',
        'a different hash value.',
      ];
      let noteY = cursorY - 10;
      noteLines.forEach(line => {
        authPage.drawText(line, { x: noteX, y: noteY, size: 9, font, color: rgb(0.3, 0.3, 0.3) });
        noteY -= 14;
      });

      cursorY -= qrSize + 24;

      // Verification URL section — split long URL across two lines to avoid overflow
      authPage.drawText('Or visit this page to verify:', {
        x: margin, y: cursorY, size: 10, font: fontBold, color: rgb(0.2, 0.2, 0.2),
      });
      cursorY -= 14;

      // Split URL into two lines of ~60 chars each so it fits within the page margins
      const urlLine1 = verificationUrl.slice(0, 72);
      const urlLine2 = verificationUrl.slice(72);

      const urlBoxHeight = urlLine2 ? 36 : 20;
      authPage.drawRectangle({
        x: margin,
        y: cursorY - urlBoxHeight + 2,
        width: pageW - margin * 2,
        height: urlBoxHeight + 6,
        color: rgb(0.94, 0.96, 1),
        borderColor: rgb(0.6, 0.7, 0.9),
        borderWidth: 0.75,
      });

      authPage.drawText(urlLine1, {
        x: margin + 6, y: cursorY - 2, size: 8, font: fontMono, color: rgb(0.1, 0.2, 0.6),
      });
      if (urlLine2) {
        authPage.drawText(urlLine2, {
          x: margin + 6, y: cursorY - 16, size: 8, font: fontMono, color: rgb(0.1, 0.2, 0.6),
        });
      }

      cursorY -= urlBoxHeight + 16;

      // Footer
      authPage.drawLine({
        start: { x: margin, y: margin + 20 },
        end: { x: pageW - margin, y: margin + 20 },
        thickness: 0.5,
        color: rgb(0.7, 0.7, 0.7),
      });
      authPage.drawText('Generated by juree.io', {
        x: margin, y: margin + 6, size: 8, font, color: rgb(0.5, 0.5, 0.5),
      });
      authPage.drawText(signedAt, {
        x: pageW - margin - 100, y: margin + 6, size: 8, font, color: rgb(0.5, 0.5, 0.5),
      });

      // --- Save final output PDF ---
      const finalPdfBytes = await outputPdf.save({ updateMetadata: false });

      // --- USB key signing (client-side, replicating Adobe's flow) ---
      // Signing is the last step — seals the complete assembled PDF.
      let finalBlob;

      if (isUsbMode) {
        const certLang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';

        const signedBytes = await signPdfWithBridge(finalPdfBytes, {
          signerName: certificateSignerName,
          signerTitle: certificateTitle,
          signerContactInfo: certificateSignerContact,
          signatureReason: certificateReason,
          signatureLocation: certificateLocation,
          signaturePageIndex: certificateAnchorPayload?.pageIndex ?? 0,
          signatureWidgetRect: certificateAnchorPayload?.widgetRect ?? null,
          signatureAppearance: { ...certificateAppearance, language: certLang },
          signatureAppearanceImage,
          signingTime,
          certId: selectedCertId,
          slot: certificateParsedInfo?.slot,
          pin: usbPinRef.current,
          certDerHex: selectedCertDerHex,
        });
        finalBlob = new Blob([signedBytes], { type: 'application/pdf' });
      } else {
        finalBlob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      }

      usbPinRef.current = ''; // Clear PIN after use

      if (onSave) {
        // For USB mode the blob is already signed — backend only stores it.
        // For .p12 mode certificateConfig carries the file and password for backend signing.
        const certificateConfig = certificateEnabled && !isUsbMode
          ? {
              enabled: true,
              certificateFile,
              certificatePassword,
              signerDisplayName: certificateSignerName,
              signerContactInfo: certificateSignerContact,
              signerTitle: certificateTitle,
              signatureReason: certificateReason,
              signatureLocation: certificateLocation,
              certificateAnchor: certificateAnchorPayload,
              usbMode: false,
              certificateAppearance: {
                ...certificateAppearance,
                language: (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en',
              },
              signatureAppearanceImage,
            }
          : null;

        await onSave(
          {
            blob: finalBlob,
            fileName: `${projectName}_signed_translations.pdf`,
            integrityHash: hashHex,
            certificateConfig,
          },
          stamps
        );
      }
    } catch (error) {
      console.error('Error saving:', error);
      setCertificateError(error.message || 'Failed to save the signed document.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col" style={{ cursor: pendingStamp ? 'none' : 'default' }}>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* PIN Dialog — appears when Finalizing in USB key mode */}
      {showPinDialog && (
        <div className="fixed inset-0 z-[99999] bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl w-96 p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">
              {t('signatureStamp.certificate.pinDialog.title', 'Sign with Certificate')}
            </h2>
            {certificateParsedInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-0.5">
                <p className="text-sm font-semibold text-blue-900">{certificateParsedInfo.commonName}</p>
                {certificateParsedInfo.title && (
                  <p className="text-xs text-blue-700">{certificateParsedInfo.title}</p>
                )}
                <p className="text-xs text-gray-500">
                  {t('signatureStamp.certificate.card.issuedBy', 'Issued by')}: {certificateParsedInfo.issuerName}
                </p>
              </div>
            )}
            <p className="text-sm text-gray-600">
              {t('signatureStamp.certificate.pinDialog.message', 'Enter your PIN code to sign this document.')}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('signatureStamp.certificate.fields.pin', 'PIN Code')}
              </label>
              <input
                type="password"
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && pinValue) {
                    e.preventDefault();
                    usbPinRef.current = pinValue;
                    setPinValue('');
                    setShowPinDialog(false);
                    handleSave();
                  }
                }}
                autoFocus
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowPinDialog(false); setPinValue(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('signatureStamp.certificate.pinDialog.cancel', 'Cancel')}
              </button>
              <button
                disabled={!pinValue}
                onClick={() => {
                  usbPinRef.current = pinValue;
                  setPinValue('');
                  setShowPinDialog(false);
                  handleSave();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('signatureStamp.certificate.pinDialog.sign', 'Sign')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* .Floating Cursor Preview for Pending Stamp */}
      {pendingStamp && (
        <div
          className="fixed pointer-events-none z-[9999] transition-all duration-150"
          style={{
            left: cursorPosition.x,
            top: cursorPosition.y,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {isValidPlacement ? (
            // Valid placement - show signature/stamp preview
            <div
              className="relative transition-all duration-150"
              style={{
                width: pendingStamp.width,
                height: pendingStamp.height,
                opacity: 0.75,
                filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.4))',
              }}
            >
              {renderStampVisual(pendingStamp)}
              <div className="absolute inset-0 border-2 border-dashed border-green-400 rounded animate-pulse"></div>
            </div>
          ) : (
            // Invalid placement - show prohibition icon
            <div
              className="relative transition-all duration-150"
              style={{
                width: Math.max(pendingStamp.width, 80),
                height: Math.max(pendingStamp.height, 80),
                filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.4))',
              }}
            >
              <div className="flex flex-col items-center justify-center w-full h-full bg-red-500 bg-opacity-20 rounded-lg border-2 border-dashed border-red-500">
                {/* Prohibition Icon */}
                <svg
                  className="w-12 h-12 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
                <span className="text-xs font-semibold text-red-700 mt-1 px-2 py-0.5 bg-white bg-opacity-90 rounded">
                  {t('signatureStamp.placement.cannotPlaceHere')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Compact Header Bar */}
      <div className="sticky top-0 z-[100] flex items-center justify-between bg-gray-800 text-white px-3 py-1.5 border-b border-gray-700 shadow-md">
        {/* Left: View Mode & Controls - Only show when signing */}
        <div className="flex items-center gap-2">
          {isSigningNewVersion && (
          <>
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-900 rounded-md p-0.5">
            <button
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                viewMode === 'side-by-side'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => setViewMode('side-by-side')}
              title="Side-by-side view"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a1 1 0 00-1 1v14a1 1 0 001 1h4m0-16v16m0-16h6m-6 16h6m0-16v16m0-16h4a1 1 0 011 1v14a1 1 0 01-1 1h-4" />
              </svg>
            </button>
            <button
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                viewMode === 'translation-only'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => setViewMode('translation-only')}
              title="Translation only"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center bg-gray-900 rounded-md p-0.5">
            <button
              onClick={() => setScale(Math.max(0.3, scale - 0.1))}
              className="px-2 py-1 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded text-xs"
              title="Zoom out"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>
            <span className="px-2 text-xs text-gray-300 font-medium min-w-[45px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale(Math.min(2.0, scale + 0.1))}
              className="px-2 py-1 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded text-xs"
              title="Zoom in"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
          </div>
          </>
          )}
        </div>

        {/* Center: Title */}
        <div className="flex-1 text-center px-4">
          <h3 className="text-sm font-semibold text-white">
            {projectName} - {isSigningNewVersion ? t('signatureStamp.title') : 'Signed Versions'}
          </h3>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          {isSigningNewVersion && (
          <>
          {saving && (
            <div className="flex items-center gap-1 text-gray-300 text-[10px] bg-gray-700 bg-opacity-50 px-1.5 py-0.5 rounded">
              <div className="w-2.5 h-2.5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
              <span>{t('signatureStamp.actions.saving')}</span>
            </div>
          )}
          <button
            onClick={() => {
              const isUsbMode = bridgeAvailable === true && selectedCertId && certificateEnabled;
              if (isUsbMode && !usbPinRef.current) {
                setShowPinDialog(true);
              } else {
                handleSave();
              }
            }}
            disabled={saving}
            className="px-2.5 py-1 bg-green-300 rounded text-xs font-medium hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
            title={t('signatureStamp.actions.save')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {saving ? t('signatureStamp.actions.saving') : t('signatureStamp.actions.finalize')}
          </button>
          </>
          )}
          <button
            onClick={onClose}
            className="p-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
            title={t('signatureStamp.actions.close')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Compact Left Sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-3 space-y-3 pb-20">
            {/* Add Signature & Stamp - Only show when in signing mode */}
            {isSigningNewVersion && (
            <div>
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">{t('signatureStamp.sidebar.addElements')}</h3>
              <div className="space-y-2">
                {userProfile?.signatureImgUrl ? (
                  <button
                    onClick={(e) => addStamp('signature', e)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-all text-left"
                  >
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">{t('signatureStamp.sidebar.signature')}</span>
                  </button>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-xs text-gray-500">{t('signatureStamp.sidebar.noSignature')}</p>
                  </div>
                )}

                {userProfile?.stampImgUrl ? (
                  <button
                    onClick={(e) => addStamp('stamp', e)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-purple-50 hover:border-purple-300 transition-all text-left"
                  >
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">{t('signatureStamp.sidebar.officialStamp')}</span>
                  </button>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-xs text-gray-500">{t('signatureStamp.sidebar.noStamp')}</p>
                  </div>
                )}

                <button
                  onClick={(e) => addStamp('conformityText', e)}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md hover:bg-emerald-50 hover:border-emerald-300 transition-all text-left"
                >
                  <svg className="w-4 h-4 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h7m-7 4h10M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">{t('signatureStamp.sidebar.conformityStamp')}</span>
                </button>

                <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md">
                  <label className="block text-xs font-semibold text-emerald-900 mb-1">
                    {t('signatureStamp.sidebar.conformityNumber')}
                  </label>
                  <input
                    type="text"
                    value={conformityNumber}
                    maxLength={24}
                    onChange={(e) => setConformityNumber(e.target.value)}
                    className="w-full text-xs px-2 py-1 border border-emerald-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  <p className="text-[10px] text-emerald-800 mt-1">{t('signatureStamp.sidebar.conformityNumberHint')}</p>
                </div>
              </div>
            </div>
            )}

            {isSigningNewVersion && (
              <div className="pt-1 border-t border-gray-200">
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">
                  {t('signatureStamp.certificate.title', 'Digital Signature')}
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={certificateEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setCertificateEnabled(checked);
                        setCertificateError('');
                        if (!checked) {
                          setStamps((prev) => prev.filter((stamp) => !isCertificateAnchorStamp(stamp)));
                        }
                      }}
                    />
                    {t('signatureStamp.certificate.enableToggle', 'Enable certificate signing')}
                  </label>

                  {certificateEnabled && (
                    <div className="space-y-2 p-2 bg-blue-50 border border-blue-200 rounded-md">

                      {/* — USB KEY MODE — */}
                      {bridgeAvailable === true && usbCertificates.length > 0 ? (
                        <>
                          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {t('signatureStamp.certificate.usbMode.detected', 'USB key detected')}
                          </div>

                          {/* Certificate cards */}
                          {usbCertificates.map((cert) => (
                            <button
                              key={cert.id}
                              onClick={() => {
                                setSelectedCertId(cert.id);
                                setSelectedCertDerHex(cert.certDerHex || '');
                                setCertificateParsedInfo(cert);
                                if (!certificateSignerName) setCertificateSignerName(cert.commonName || '');
                                setCertificateTitle(cert.title || '');
                              }}
                              className={`w-full text-left p-2 rounded-md border transition-all ${
                                selectedCertId === cert.id
                                  ? 'bg-blue-100 border-blue-500'
                                  : 'bg-white border-blue-200 hover:border-blue-400'
                              }`}
                            >
                              <p className="text-[12px] font-bold text-blue-900 leading-tight">{cert.commonName}</p>
                              {cert.title && <p className="text-[10px] text-blue-700">{cert.title}</p>}
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {t('signatureStamp.certificate.card.issuedBy', 'Issued by')}: {cert.issuerName}
                              </p>
                              {cert.validTo && (
                                <p className="text-[10px] text-gray-500">
                                  {t('signatureStamp.certificate.card.validUntil', 'Valid until')}:{' '}
                                  {new Date(cert.validTo).toLocaleDateString()}
                                </p>
                              )}
                              {selectedCertId === cert.id && (
                                <span className="inline-block mt-1 text-[9px] font-semibold text-blue-600 uppercase tracking-wide">
                                  ● {t('signatureStamp.certificate.card.selected', 'Selected')}
                                </span>
                              )}
                            </button>
                          ))}
                        </>
                      ) : bridgeAvailable === true && usbCertificates.length === 0 ? (
                        /* — BRIDGE RUNNING, NO TOKEN — */
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 space-y-2">
                          <p className="text-[11px] font-semibold text-amber-800">
                            {t('signatureStamp.certificate.usbMode.noToken', 'Juree Signing Bridge is running, but no USB certificate was found')}
                          </p>
                          <p className="text-[10px] text-amber-700">
                            {t('signatureStamp.certificate.usbMode.insertToken', 'Insert the USB token, make sure the SafeNet/CertEurope driver is installed, then rescan.')}
                          </p>
                          <button
                            type="button"
                            onClick={() => detectUsbBridge({ showChecking: true })}
                            className="text-[10px] font-semibold text-amber-800 underline hover:text-amber-900"
                          >
                            {t('signatureStamp.certificate.usbMode.rescan', 'Rescan USB bridge')}
                          </button>
                        </div>
                      ) : bridgeAvailable === false ? (
                        /* — BRIDGE NOT DETECTED — */
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 space-y-2">
                          <p className="text-[11px] font-semibold text-amber-800">
                            {t('signatureStamp.certificate.usbMode.notDetected', 'USB signing bridge not detected')}
                          </p>
                          <p className="text-[10px] text-amber-700">
                            {t('signatureStamp.certificate.usbMode.installBridge', 'Start Juree Signing Bridge for USB key signing, then rescan. You can also use a .p12 file below.')}
                          </p>
                          <button
                            type="button"
                            onClick={() => detectUsbBridge({ showChecking: true })}
                            className="text-[10px] font-semibold text-amber-800 underline hover:text-amber-900"
                          >
                            {t('signatureStamp.certificate.usbMode.rescan', 'Rescan USB bridge')}
                          </button>
                        </div>
                      ) : (
                        /* — CHECKING — */
                        <p className="text-[11px] text-blue-700 animate-pulse">Detecting USB key…</p>
                      )}

                      {/* Fallback: .p12 upload (always shown when bridge is not available) */}
                      {bridgeAvailable !== true && (
                        <div>
                          <label className="block text-[11px] font-semibold text-blue-900 mb-1">
                            {t('signatureStamp.certificate.fields.file', 'Certificate file (.p12/.pfx)')}
                          </label>
                          <input
                            type="file"
                            accept=".p12,.pfx,application/x-pkcs12"
                            onChange={(e) => setCertificateFile(e.target.files?.[0] || null)}
                            className="w-full text-[11px]"
                          />
                        </div>
                      )}

                      {/* Signature details — shared by both modes */}
                      <div>
                        <label className="block text-[11px] font-semibold text-blue-900 mb-1">
                          {t('signatureStamp.certificate.fields.signerName', 'Name')}
                        </label>
                        <input
                          type="text"
                          value={certificateSignerName}
                          onChange={(e) => setCertificateSignerName(e.target.value)}
                          className="w-full text-xs px-2 py-1 border border-blue-300 rounded"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-blue-900 mb-1">
                          {t('signatureStamp.certificate.fields.title', 'Title')}
                        </label>
                        <input
                          type="text"
                          value={certificateTitle}
                          onChange={(e) => setCertificateTitle(e.target.value)}
                          className="w-full text-xs px-2 py-1 border border-blue-300 rounded"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-blue-900 mb-1">
                          {t('signatureStamp.certificate.fields.location', 'Location')}
                        </label>
                        <input
                          type="text"
                          value={certificateLocation}
                          onChange={(e) => setCertificateLocation(e.target.value)}
                          className="w-full text-xs px-2 py-1 border border-blue-300 rounded"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-blue-900 mb-1">
                          {t('signatureStamp.certificate.fields.reason', 'Reason')}
                        </label>
                        <input
                          type="text"
                          value={certificateReason}
                          onChange={(e) => setCertificateReason(e.target.value)}
                          className="w-full text-xs px-2 py-1 border border-blue-300 rounded"
                        />
                      </div>

                      {/* Password — only shown in .p12 fallback mode */}
                      {bridgeAvailable !== true && (
                        <div>
                          <label className="block text-[11px] font-semibold text-blue-900 mb-1">
                            {t('signatureStamp.certificate.fields.password', 'Certificate password')}
                          </label>
                          <input
                            type="password"
                            value={certificatePassword}
                            onChange={(e) => setCertificatePassword(e.target.value)}
                            className="w-full text-xs px-2 py-1 border border-blue-300 rounded"
                          />
                        </div>
                      )}

                      {/* Appearance checkboxes */}
                      <div className="grid grid-cols-2 gap-1 text-[11px] text-gray-700 pt-1 border-t border-blue-100">
                        <label className="flex items-center gap-1"><input type="checkbox" checked={certificateAppearance.includeName} onChange={(e) => setCertificateAppearance(prev => ({ ...prev, includeName: e.target.checked }))} />{t('signatureStamp.certificate.appearance.includeName', 'Name')}</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={certificateAppearance.includeDistinguishedName} onChange={(e) => setCertificateAppearance(prev => ({ ...prev, includeDistinguishedName: e.target.checked }))} />{t('signatureStamp.certificate.appearance.includeDistinguishedName', 'DN')}</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={certificateAppearance.includeDate} onChange={(e) => setCertificateAppearance(prev => ({ ...prev, includeDate: e.target.checked }))} />{t('signatureStamp.certificate.appearance.includeDate', 'Date')}</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={certificateAppearance.includeLocation} onChange={(e) => setCertificateAppearance(prev => ({ ...prev, includeLocation: e.target.checked }))} />{t('signatureStamp.certificate.appearance.includeLocation', 'Location')}</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={certificateAppearance.includeReason} onChange={(e) => setCertificateAppearance(prev => ({ ...prev, includeReason: e.target.checked }))} />{t('signatureStamp.certificate.appearance.includeReason', 'Reason')}</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={certificateAppearance.includeLabels} onChange={(e) => setCertificateAppearance(prev => ({ ...prev, includeLabels: e.target.checked }))} />{t('signatureStamp.certificate.appearance.includeLabels', 'Labels')}</label>
                      </div>

                      {/* Anchor placement */}
                      <button
                        onClick={(e) => addStamp(CERTIFICATE_ANCHOR_TYPE, e)}
                        className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded-md hover:bg-blue-100 transition-all text-left mt-1"
                      >
                        <svg className="w-4 h-4 text-blue-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7H7a2 2 0 00-2 2v10h14V9a2 2 0 00-2-2zm0 0V5a2 2 0 00-2-2H9a2 2 0 00-2 2v2h10z" />
                        </svg>
                        <span className="text-xs font-semibold text-blue-900">{t('signatureStamp.certificate.placeAnchor', 'Place Digital Signature Anchor')}</span>
                      </button>
                      <p className="text-[11px] text-blue-800">
                        {certificateAnchorStamps.length > 1
                          ? t('signatureStamp.certificate.anchorCount', {
                              count: certificateAnchorStamps.length,
                              defaultValue: '{{count}} digital signature anchors placed',
                            })
                          : getCertificateAnchorStamp()
                          ? t('signatureStamp.certificate.anchorPlaced', {
                              page: getCertificateAnchorStamp().pageIndex + 1,
                              documentType:
                                getCertificateAnchorStamp().documentType === 'original'
                                  ? t('signatureStamp.documentTypes.orig', 'Orig')
                                  : t('signatureStamp.documentTypes.trans', 'Trans'),
                              defaultValue: 'Anchor: page {{page}} ({{documentType}})',
                            })
                          : t('signatureStamp.certificate.anchorNotPlaced', 'Anchor not placed yet')}
                      </p>

                      {certificateError && (
                        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                          {certificateError}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Active Stamps - Only show when in signing mode */}
            {isSigningNewVersion && stamps.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">
                  {t('signatureStamp.sidebar.active')} ({stamps.length})
                </h3>
                <div className="space-y-1">
                  {stamps.map(stamp => (
                    <div
                      key={stamp.id}
                      className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <svg className={`w-3.5 h-3.5 flex-shrink-0 ${stamp.type === 'stamp' ? 'text-purple-600' : stamp.type === 'conformityText' ? 'text-emerald-700' : stamp.type === CERTIFICATE_ANCHOR_TYPE ? 'text-emerald-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {stamp.type === 'stamp' ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          ) : stamp.type === 'conformityText' ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h7m-7 4h10M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
                          ) : stamp.type === CERTIFICATE_ANCHOR_TYPE ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7H7a2 2 0 00-2 2v10h14V9a2 2 0 00-2-2zm0 0V5a2 2 0 00-2-2H9a2 2 0 00-2 2v2h10z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          )}
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">{stamp.name}</p>
                          <p className="text-[10px] text-gray-500">
                            {documentPairs.find(p => p.id === stamp.documentId)?.originalFileName || t('common.unknown')} ({stamp.documentType === 'original' ? t('signatureStamp.documentTypes.orig') : t('signatureStamp.documentTypes.trans')}), {t('signatureStamp.document.page')} {stamp.pageIndex + 1}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeStamp(stamp.id)}
                        className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                        title={t('signatureStamp.sidebar.remove')}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Signed Versions List - Show below stamps */}
            {finalDocuments && finalDocuments.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">
                  Signed Versions ({finalDocuments.length})
                </h3>
                <div className="space-y-2">
                  {[...finalDocuments]
                    .sort((a, b) => b.version - a.version)
                    .map(version => (
                      <button
                        key={version.id}
                        onClick={() => {
                          setSelectedVersionId(version.id);
                          setIsSigningNewVersion(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 border rounded-md transition-all text-left ${
                          selectedVersionId === version.id && !isSigningNewVersion
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {`Version ${version.version} • ${getVersionSentLabel(version.sentAt)}`}
                          </p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {new Date(version.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </button>
                    ))}
                </div>

                {/* Sign New Version Button */}
                {!isSigningNewVersion && (
                  <button
                    onClick={() => setIsSigningNewVersion(true)}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 bg-white hover:bg-green-50 hover:border-green-300 rounded-md transition-all"
                  >
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Sign New Version</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className={`flex-1 bg-gray-100 overflow-auto ${isSigningNewVersion ? 'p-6' : ''}`} ref={containerRef}>
          {/* Display selected version when not in signing mode */}
          {!isSigningNewVersion && selectedVersionId && (() => {
            const selectedVersion = finalDocuments.find(v => v.id === selectedVersionId);
            const pdfData = versionPdfData[selectedVersionId];
            const selectedVersionDownloadUrl = getVersionDownloadUrl(selectedVersion);

            return (
              <div className="h-full flex flex-col">
                {/* Compact Header - Version info in one line */}
                <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-800">
                      Version {selectedVersion?.version}
                    </span>
                    <span className="text-xs text-gray-500">
                      {selectedVersion?.createdAt ? new Date(selectedVersion.createdAt).toLocaleString() : ''}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getVersionSentLabel(selectedVersion?.sentAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedVersionDownloadUrl && (
                      <a
                        href={selectedVersionDownloadUrl}
                        download={selectedVersion?.fileName || undefined}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg transition-colors"
                        title={t('projectDetails.finalDocument.downloadPdf', 'Download')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('projectDetails.finalDocument.downloadPdf', 'Download')}
                      </a>
                    )}
                    {onSendToClient && selectedVersion && (
                      <button
                        onClick={() => onSendToClient(selectedVersion)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        title={t('projectDetails.actions.sendToClient', 'Send to Client')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('projectDetails.actions.sendToClient', 'Send to Client')}
                      </button>
                    )}
                  </div>
                </div>

                {/* Maximized PDF Viewer */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-100">
                  {pdfData ? (
                    <Document
                      file={pdfData}
                      onLoadSuccess={(pdf) => {
                        setVersionPdfPages(pdf.numPages);
                        console.log('PDF loaded successfully:', pdf.numPages, 'pages');
                      }}
                      onLoadError={(error) => {
                        console.error('Failed to load PDF:', error);
                      }}
                      loading={
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-sm text-gray-600">Loading document...</p>
                          </div>
                        </div>
                      }
                      error={
                        <div className="flex items-center justify-center min-h-screen">
                          <div className="text-center text-red-400">
                            <p className="text-lg font-semibold mb-2">Failed to load document</p>
                            <a
                              href={selectedVersion?.dropboxUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline"
                            >
                              Download PDF instead
                            </a>
                          </div>
                        </div>
                      }
                    >
                      <div className="py-4">
                        <div className="space-y-8 flex flex-col items-center">
                          {versionPdfPages > 0 && Array.from({ length: versionPdfPages }, (_, i) => i + 1).map(pageNumber => (
                            <div key={pageNumber} className="bg-white border border-gray-300">
                              <Page
                                pageNumber={pageNumber}
                                width={Math.min(1000, window.innerWidth - 350)}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </Document>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-sm text-gray-600">Loading PDF from Dropbox...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Document signing interface - only show when in signing mode */}
          {isSigningNewVersion && (
          <div className="space-y-8">
            {documentPairs.map((pair, pairIndex) => {
              const originalPages = docPageCounts[`${pair.id}-original`] || 0;
              const translationPages = docPageCounts[`${pair.id}-translation`] || 0;
              const a4RenderWidth = viewMode === 'translation-only' ? window.innerWidth - 350 : (window.innerWidth - 350) / 2;
              const a4RenderHeight = a4RenderWidth * (A4_SIZE.height / A4_SIZE.width);

              return (
                <div
                  key={pair.index}
                  ref={(el) => documentRefs.current[pairIndex] = el}
                  draggable={!isResizingStamp && !isHoveringStamp}
                  onDragStart={(e) => {
                    // Prevent drag if resizing or hovering over a stamp
                    if (isResizingStamp || isHoveringStamp) {
                      e.preventDefault();
                      return;
                    }
                    // Check if drag started from stamp element
                    const target = e.target;
                    if (target.closest('.stamp-element')) {
                      e.preventDefault();
                      return;
                    }
                    handleDragStart(e, pairIndex);
                  }}
                  onDragEnter={(e) => handleDragEnter(e, pairIndex)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  className={`flex justify-center items-stretch transition-all duration-200 ${
                    draggedIndex === pairIndex ? 'opacity-40 scale-95' : 'opacity-100'
                  } ${!isDraggingDoc && !isResizingStamp && !isHoveringStamp ? 'cursor-grab hover:shadow-2xl' : ''} ${isDraggingDoc ? 'cursor-grabbing' : ''}`}
                >
                  {/* LEFT: Original */}
                  {viewMode === 'side-by-side' && (
                    <div className="w-1/2" data-document-id={pair.id} data-document-type="original">
                      <div className="bg-white rounded-lg shadow-xl border-4 border-gray-300 p-4 h-full">
                        <h3 className="text-lg font-bold text-gray-800 mb-3 text-center border-b-2 border-gray-200 pb-2">
                          📄 {t('signatureStamp.document.original')} - {pair.originalFileName}
                        </h3>
                        <div className="overflow-x-auto overflow-y-visible hide-scrollbar bg-gray-100 p-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                          {pair.originalUrl ? (
                            isImageFile(pair.originalUrl, pair.originalFileName) ? (
                              <div className="mb-8 flex justify-center">
                                <div
                                  className="relative bg-white border border-gray-300"
                                  data-stamp-surface
                                  data-page-number="1"
                                  data-document-id={pair.id}
                                  data-document-type="original"
                                >
                                  {/* Pixel (not %) max-width/max-height so the image sizes itself to its
                                      own natural fit-contain box instead of relying on a parent size —
                                      this wrapper then shrink-wraps that box exactly (no letterboxing),
                                      matching the translation-side surface below. Block display avoids
                                      the few px of inline baseline gap that would otherwise throw the
                                      wrapper's measured height off by a hair. */}
                                  <img
                                    src={pair.originalUrl}
                                    alt={pair.originalFileName}
                                    className="block"
                                    style={{ maxWidth: a4RenderWidth, maxHeight: a4RenderHeight, width: 'auto', height: 'auto' }}
                                  />

                                  {/* Stamps on image (A4 space) */}
                                  {stamps
                                    .filter(s => s.documentId === pair.id && s.pageIndex === 0 && s.documentType === 'original')
                                    .map(stamp => (
                                      <Rnd
                                        key={stamp.id}
                                        size={{ width: stamp.width, height: stamp.height }}
                                        position={{ x: stamp.x, y: stamp.y }}
                                        disableDragging={true}
                                        minWidth={getStampMinWidth(stamp)}
                                        minHeight={getStampMinHeight(stamp)}
                                        onResizeStart={() => {
                                          setIsResizingStamp(true);
                                        }}
                                        onResize={(_e, _direction, ref) => {
                                          // Provide real-time visual feedback during resize
                                          updateStampSize(stamp.id, parseInt(ref.style.width), parseInt(ref.style.height));
                                        }}
                                        onResizeStop={() => {
                                          setIsResizingStamp(false);
                                        }}
                                        bounds="parent"
                                        className={`stamp-element border-2 border-dashed ${isCertificateAnchorStamp(stamp) ? 'border-emerald-400 hover:border-emerald-600' : 'border-blue-400 hover:border-blue-600'}`}
                                        style={{ zIndex: 10 }}
                                      >
                                        <div
                                          className="relative w-full h-full group cursor-pointer"
                                          onMouseEnter={() => setIsHoveringStamp(true)}
                                          onMouseLeave={() => setIsHoveringStamp(false)}
                                          onMouseDown={(e) => handleStampMouseDown(stamp, e)}
                                          onDoubleClick={(e) => startRepositioning(stamp, e)}
                                        >
                                          {renderStampVisual(stamp)}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeStamp(stamp.id);
                                            }}
                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs font-bold shadow-lg z-10"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </Rnd>
                                    ))}
                                </div>
                              </div>
                            ) : (
                              <Document
                                file={pair.originalUrl}
                                onLoadSuccess={(pdf) => {
                                  updatePageCount(pair.id, 'original', pdf.numPages);
                                  pdfDocumentRefs.current[`${pair.id}-original`] = pdf;
                                }}
                                onLoadError={(error) => {
                                  console.error('Failed to load original PDF:', error);
                                }}
                                loading={<div className="p-8 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-gray-600 mx-auto"></div></div>}
                                error={<div className="p-8 text-center text-gray-400"><p>{t('signatureStamp.document.failedToLoad')}</p></div>}
                              >
                                {Array.from({ length: originalPages }, (_, pageIdx) => {
                                  const pageSize = docPageDimensions[`${pair.id}-original-${pageIdx}`];
                                  const fit = pageSize
                                    ? getFitContain(pageSize.width, pageSize.height, a4RenderWidth, a4RenderHeight)
                                    : { scale: 1, drawW: a4RenderWidth, drawH: a4RenderHeight };
                                  const pageWidth = pageSize ? pageSize.width * fit.scale : a4RenderWidth;

                                  return (
                                    <div key={`orig-${pair.id}-${pageIdx}`} className="mb-8 flex justify-center">
                                      {/* Sized to the actual fit-contain box (pageWidth x fit.drawH), not a
                                          fixed A4-shaped box — matches the translation-side surface below,
                                          which has always used this pattern. A forced box here let the
                                          rendered page be smaller than its container and centered inside
                                          it (letterboxed); stamp placement math measures this element's own
                                          bounds directly, so any gap between it and the visible page content
                                          silently threw off every stamp's exported position. */}
                                      <div
                                        className="bg-white border border-gray-300 relative"
                                        style={{ width: pageWidth, height: fit.drawH }}
                                        data-stamp-surface
                                        data-page-number={pageIdx + 1}
                                        data-document-id={pair.id}
                                        data-document-type="original"
                                      >
                                        <Page
                                          pageNumber={pageIdx + 1}
                                          width={pageWidth}
                                          renderTextLayer={false}
                                          renderAnnotationLayer={false}
                                          onLoadSuccess={(page) => {
                                            const viewport = page.getViewport({ scale: 1 });
                                            setDocPageDimensions(prev => ({
                                              ...prev,
                                              [`${pair.id}-original-${pageIdx}`]: { width: viewport.width, height: viewport.height }
                                            }));
                                          }}
                                        />

                                        {/* Stamps on original (A4 space) */}
                                        {stamps
                                          .filter(s => s.documentId === pair.id && s.pageIndex === pageIdx && s.documentType === 'original')
                                          .map(stamp => (
                                            <Rnd
                                              key={stamp.id}
                                              size={{ width: stamp.width, height: stamp.height }}
                                              position={{ x: stamp.x, y: stamp.y }}
                                              disableDragging={true}
                                              minWidth={getStampMinWidth(stamp)}
                                              minHeight={getStampMinHeight(stamp)}
                                              onResizeStart={() => {
                                                setIsResizingStamp(true);
                                              }}
                                              onResize={(_e, _direction, ref) => {
                                                // Provide real-time visual feedback during resize
                                                updateStampSize(stamp.id, parseInt(ref.style.width), parseInt(ref.style.height));
                                              }}
                                              onResizeStop={() => {
                                                setIsResizingStamp(false);
                                              }}
                                              bounds="parent"
                                              className={`stamp-element border-2 border-dashed ${isCertificateAnchorStamp(stamp) ? 'border-emerald-400 hover:border-emerald-600' : 'border-blue-400 hover:border-blue-600'}`}
                                              style={{ zIndex: 10 }}
                                            >
                                              <div
                                                className="relative w-full h-full group cursor-pointer"
                                                onMouseEnter={() => setIsHoveringStamp(true)}
                                                onMouseLeave={() => setIsHoveringStamp(false)}
                                                onMouseDown={(e) => handleStampMouseDown(stamp, e)}
                                                onDoubleClick={(e) => startRepositioning(stamp, e)}
                                              >
                                                {renderStampVisual(stamp)}
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeStamp(stamp.id);
                                                  }}
                                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs font-bold shadow-lg z-10"
                                                >
                                                  ✕
                                                </button>
                                              </div>
                                            </Rnd>
                                          ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </Document>
                            )
                          ) : (
                            <div className="p-12 text-center text-gray-400"><p>{t('signatureStamp.document.noOriginal')}</p></div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* RIGHT: Translation with stamps */}
                  <div className={viewMode === 'translation-only' ? 'w-full' : 'w-1/2'} data-document-id={pair.id} data-document-type="translation">
                    <div className="bg-white rounded-lg shadow-xl border-4 border-blue-500 p-4 h-full">
                      <h3 className="text-lg font-bold text-blue-800 mb-3 text-center border-b-2 border-blue-200 pb-2">
                        🌐 {t('signatureStamp.document.translation')} - {pair.originalFileName}
                      </h3>
                      <div className="overflow-x-auto overflow-y-visible hide-scrollbar bg-gray-100 p-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {pair.translationFileObj ? (
                          <Document
                            file={pair.translationFileObj}
                            onLoadSuccess={(pdf) => {
                              updatePageCount(pair.id, 'translation', pdf.numPages);
                              pdfDocumentRefs.current[`${pair.id}-translation`] = pdf;
                              // Store the rendered width for this document
                              const renderedWidth = viewMode === 'translation-only' ? window.innerWidth - 350 : (window.innerWidth - 350) / 2;
                              setDocPageDimensions(prev => ({
                                ...prev,
                                [pair.id]: { renderedWidth }
                              }));
                            }}
                            onLoadError={(error) => {
                              console.error('Failed to load translation PDF:', error);
                            }}
                            loading={<div className="p-8 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div><p className="text-sm text-gray-600 mt-2">{t('signatureStamp.document.loading')}</p></div>}
                            error={<div className="p-8 text-center text-red-400"><p>{t('signatureStamp.document.failedToLoadTranslation')}</p></div>}
                          >
                            {Array.from({ length: translationPages }, (_, pageIdx) => (
                              <div key={`trans-${pair.id}-${pageIdx}`} className="mb-8 flex justify-center">
                                <div
                                  className="relative bg-white border border-gray-300"
                                  data-stamp-surface
                                  data-page-number={pageIdx + 1}
                                  data-document-id={pair.id}
                                  data-document-type="translation"
                                >
                                  <Page
                                    pageNumber={pageIdx + 1}
                                    width={viewMode === 'translation-only' ? window.innerWidth - 350 : (window.innerWidth - 350) / 2}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                  />

                                  {/* Stamps */}
                                  {stamps
                                    .filter(s => s.documentId === pair.id && s.pageIndex === pageIdx && s.documentType === 'translation')
                                    .map(stamp => (
                                      <Rnd
                                        key={stamp.id}
                                        size={{ width: stamp.width, height: stamp.height }}
                                        position={{ x: stamp.x, y: stamp.y }}
                                        disableDragging={true}
                                        minWidth={getStampMinWidth(stamp)}
                                        minHeight={getStampMinHeight(stamp)}
                                        onResizeStart={() => {
                                          setIsResizingStamp(true);
                                        }}
                                        onResize={(_e, _direction, ref) => {
                                          // Provide real-time visual feedback during resize
                                          updateStampSize(stamp.id, parseInt(ref.style.width), parseInt(ref.style.height));
                                        }}
                                        onResizeStop={() => {
                                          setIsResizingStamp(false);
                                        }}
                                        bounds="parent"
                                        className={`stamp-element border-2 border-dashed ${isCertificateAnchorStamp(stamp) ? 'border-emerald-400 hover:border-emerald-600' : 'border-blue-400 hover:border-blue-600'}`}
                                        style={{ zIndex: 10 }}
                                      >
                                        <div
                                          className="relative w-full h-full group cursor-pointer"
                                          onMouseEnter={() => setIsHoveringStamp(true)}
                                          onMouseLeave={() => setIsHoveringStamp(false)}
                                          onMouseDown={(e) => handleStampMouseDown(stamp, e)}
                                          onDoubleClick={(e) => startRepositioning(stamp, e)}
                                        >
                                          {renderStampVisual(stamp)}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeStamp(stamp.id);
                                            }}
                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs font-bold shadow-lg z-10"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </Rnd>
                                    ))}
                                </div>
                              </div>
                            ))}
                          </Document>
                        ) : (
                          <div className="p-12 text-center text-gray-400"><p>{t('signatureStamp.document.noTranslation')}</p></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureStampView;
